import { useChangePolicy } from '../../hooks/useChangePolicy';
import AccountSnapshot from '../../components/AccountSnapshot';
import { inStartWindow, startWindowDisabledTime, START_WINDOWS_TEXT } from '../../utils/startWindow';
import { isTerminalAccount, TERMINAL_START_MESSAGE } from '../../utils/terminalAccount';
import DesktopAppLink, { DESKTOP_APP_PATH } from '../../components/DesktopAppLink';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, AutoComplete, Table, InputNumber, Spin, Select, Button, message, Modal, Tooltip, App, Popconfirm, Popover, TimePicker, Switch, ConfigProvider } from 'antd';
import { SearchOutlined, PlusOutlined, DeleteOutlined, EditOutlined, QuestionCircleOutlined, StopOutlined, SaveOutlined, ThunderboltOutlined, ArrowDownOutlined, ArrowUpOutlined, ClockCircleOutlined, ImportOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import {
    Container,
    Card,
    Grid,
    FormGroup,
    Label,
    BuyButton,
    SellButton,
    LegTableWrapper,
    PairConfigScroll,
    ProportionValue,
    EditProportionButton,
} from './styles';
import { FutureInstrument, StockInstrument, PublishedStrategy } from '../../types';
import pairTradingApi, { PairCashCapacity, PairAccountingPair, PairBotOrder, PairPendingImport } from '../../services/pairTradingApi';
import { ImportInProgress, importInProgressOf } from '../../services/importJobApi';
import ImportJobProgress from '../../components/ImportJobProgress';
import ImportInProgressModal from '../../components/ImportJobProgress/ImportInProgressModal';
import { usePairConfigLoader } from '../../hooks/usePairConfigLoader';
import { usePortfolios } from '../../hooks/usePortfolios';
import { usePairList } from '../../hooks/usePairList';
import { usePairUrlState } from '../../hooks/usePairUrlState';
import PairSwitcherBar from './components/PairSwitcherBar';
import PairLifecyclePanel from './components/PairLifecyclePanel';
import EntryModeToggle from './components/EntryModeToggle';
import { LegSymbolCell } from './components/LegCells';
import {
    NEW_PAIR_KEY,
    DEFAULT_PAIR_FORM,
    PairFormSnapshot,
    PairOpenMode,
    copySnapshotForNewPair,
    isPairReadOnly,
    loadPairDrafts,
    savePairDrafts,
} from './utils/pairSelection';
import { useExecutionStatus, isBotUp } from '../../hooks/useExecutionStatus';
import TradingPageHeader from '../../components/TradingPageHeader';
import OnboardingTour from '../../components/OnboardingTour';
import { PAIR_ONBOARDING_DEMO_STOCK, PAIR_ONBOARDING_KEY, pairOnboardingSteps } from './onboardingSteps';
import { useWebSocketPriceData } from '../../hooks/useWebSocketPriceData';
import { useLivePrices, livePriceOf } from '../../hooks/useLivePrices';
import { useDefaultFuturePrice } from '../../hooks/useDefaultFuturePrice';
import { useBalanceInfo } from '../../hooks/useBalanceInfo';
import { usePlacePairStrategy } from '../../hooks/usePlacePairStrategy';
import { useStockSearch } from '../../hooks/useStockSearch';
import CandlestickChart from '../../components/CandlestickChart';
import BidAsk from '../../components/BidAsk';
import OrderAndTransactionsTab from './tabs/OrderAndTransactionsTab';
import PortfolioTab from './tabs/PortfolioTab';
import PairDepositWithdrawModal from './components/PairDepositWithdrawModal';
import PnlCellDisplay from './components/PnlCellDisplay';
import RollDerivLegModal from './components/RollDerivLegModal';
import ReferencePnlBanner from './components/ReferencePnlBanner';
import CorporateActionPanel from '../../components/CorporateActionPanel';
import FuturesRollBanner from './components/FuturesRollBanner';
import RollPlanModal from './components/RollPlanModal';
import PairImportModal from './components/PairImportModal';
import usePairRollPlan from '../../hooks/usePairRollPlan';
import { planSummary } from '../../utils/rollPlanView';
import LogViewer from '../../components/LogViewer';
import { ChartTabsSplit, ChartConfigSplit } from '../../components/ResponsiveTradingLayout';
import { MobileTradingShell, ShellSection, UnsavedDot } from '../../components/mobile';
import useIsMobile from '../../hooks/useIsMobile';
import { DEFAULT_DERIV_MIN_MARGIN_RATE, DEFAULT_DERIV_MULTIPLIER } from '../../utils/pairAllocationMath';
import { computePairReferencePnl } from '../../utils/pairReferencePnl';
import { chartContractNote, resolveChartContract } from './resolveChartContract';
import { computePositionPnl } from '../../utils/pairPositionPnl';
import { diffPairSettings } from './utils/pairSettingsDiff';
import {
    resolvePnlCell,
    engineStocksByTicker,
    resolveReferenceSource,
} from '../../utils/pairPnlDisplay';
import { usePairReferencePnl } from '../../hooks/usePairReferencePnl';
import { usePairAllocationBalance } from '../../hooks/usePairAllocationBalance';
import { usePnlHistory, type PnlInterval } from '../../hooks/usePnlHistory';
import { usePairSpreadHistory } from '../../hooks/usePairSpreadHistory';
import { spreadBasket, spreadPercent } from '../../utils/pairSpreadHistory';
import PairPnlChart from '../../components/PairPnlChart';
import {
    aggregateReturnPct,
    computeLegNotional,
    computePairMetrics,
} from '../../utils/pairPnlSeries';
import ChartViewToggle from '../../components/ChartViewToggle';
import PriceAge from '../../components/PriceAge';
import { isExpiredContract, futureExpiry, formatExpiry, nextContract, manualRollBlock } from '../../utils/futuresExpiry';
import { formatNumber, formatInt, formatDecimal, formatCompact, formatAbbrev } from '../../utils/numberFormat';


/** While the pair runs, fills move orders and balances: re-read the tabs this often. */
const PAIR_LIVE_POLL_MS = 15_000;

const coreService = apiService.getService(ServiceType.CORE);

/**
 * Flash volume limit is built end to end but not released: the cap is left unset so the
 * engine uses its own default. Hidden rather than deleted — flip this to true to bring its
 * column back. It used to live in ProportionModal, which this page no longer has: the two
 * fields that modal edited (proportion, target price) are typed in the table itself.
 */
const SHOW_FLASH_VOLUME_LIMIT = false;

const PairTrading: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Get strategyId and executionId from navigation state. strategyId may be
    // absent on a direct load / refresh, so fall back to the published
    // PAIR_TRADING strategy (fetched below) — otherwise the daily-schedule
    // button stays disabled because it needs a strategy id to POST /daily.
    const routeStrategyId = useMemo(() => {
        return (location.state as any)?.strategyId || null;
    }, [location.state]);
    const [fetchedPairStrategyId, setFetchedPairStrategyId] = useState<string | null>(null);
    const strategyId = routeStrategyId || fetchedPairStrategyId;

    const executionId = useMemo(() => {
        return (location.state as any)?.executionId || null;
    }, [location.state]);

    // Resolve the published PAIR strategy id when the route didn't carry one
    // (e.g. direct load / refresh) so scheduling works without re-navigating.
    useEffect(() => {
        if (routeStrategyId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await coreService.get<{ data?: PublishedStrategy[] }>(
                    '/strategies/published', { params: { type: 'PAIR_TRADING' } },
                );
                const list = (res.data && (res.data as any).data)
                    ? (res.data as any).data
                    : (Array.isArray(res.data) ? res.data : []);
                const pair = list.find(
                    (s: PublishedStrategy) => s.type === 'PAIR_TRADING' || s.type === 'PAIR',
                ) ?? list[0];
                if (!cancelled && pair?.id) setFetchedPairStrategyId(pair.id);
            } catch (e) {
                console.error('Failed to fetch pair strategy for scheduling', e);
            }
        })();
        return () => { cancelled = true; };
    }, [routeStrategyId]);

    // State management
    const [activeTab, setActiveTab] = useState('1');
    // Responsive: below 992px the config column moves into a bottom Drawer.
    const isMobile = useIsMobile(992);
    const [mobileConfigOpen, setMobileConfigOpen] = useState(false);
    /** Which pill the compact shell is showing. The config groups share one card. */
    const [pairMobileSection, setPairMobileSection] = useState<string>('legs');
    // Sub-tab below the candlestick chart, grid-style: Logs (first) / Position / Order Book.
    const [bottomTab, setBottomTab] = useState('logs');
    const [pairPositions, setPairPositions] = useState<PairAccountingPair[]>([]);
    // A Sync the bot has not taken: the positions below are its book from before it.
    const [pendingImport, setPendingImport] = useState<PairPendingImport | null>(null);
    // Start refused by an open import: its progress, in a modal
    const [startImportConflict, setStartImportConflict] = useState<ImportInProgress | null>(null);
    // Collapsed pair blocks in the Position tab (dropdown). A pairId in the set
    // is collapsed; default (absent) is expanded.
    const [collapsedPairs, setCollapsedPairs] = useState<Set<string>>(new Set());
    const togglePairCollapsed = (pairId: string) => {
        setCollapsedPairs((prev) => {
            const next = new Set(prev);
            if (next.has(pairId)) next.delete(pairId);
            else next.add(pairId);
            return next;
        });
    };
    const [loadingPositions, setLoadingPositions] = useState(false);
    const [showTabs, setShowTabs] = useState(true);
    const [selectedFuture, setSelectedFuture] = useState<FutureInstrument | null>(null);
    const [selectedStocks, setSelectedStocks] = useState<StockInstrument[]>([]);

    // Use VN30 price hook

    // Use WebSocket price data hook for the selected future
    const { priceData: futurePriceData } = useWebSocketPriceData({
        symbol: selectedFuture?.symbol || null,
        enabled: !!selectedFuture?.symbol
    });

    // Trading parameters
    // RETURN (close on TP/CL) is the only mode the user can pick. FORCED used to be
    // exposed as a "Force Close" switch, but `close_type` is not runtime-patchable
    // — toggling it on a running bot just 409'd, and stopping the bot does not close
    // anything. Closing on demand goes through the Flatten action instead
    // (POST /pair/:dc/flatten), which the worker can actually service.
    const closeType = 'RETURN';
    const [takeProfitReturn, setTakeProfitReturn] = useState<number>(0);
    const [cutLossReturn, setCutLossReturn] = useState<number>(0);
    const [maxOpen, setMaxOpen] = useState<number>(1);
    // Only the setter is read (by usePairConfigLoader); the boolean itself isn't
    // rendered after we moved the Stop button to PortfolioTab.
    const [, setIsStarting] = useState<boolean>(false);
    const [stockProportion, setStockProportion] = useState<number>(50);
    // v3 reference-PnL trigger controls (replaced open_point / close_point).
    const [activeSide, setActiveSide] = useState<'BUY' | 'SELL'>('SELL');
    const [futureInstrumentPrice, setFutureInstrumentPrice] = useState<number | null>(null);
    const [isPause, setIsPause] = useState<boolean>(false);
    const [openMode, setOpenMode] = useState<PairOpenMode>(DEFAULT_PAIR_FORM.openMode);
    // Reference prices only drive the REFERENCE trigger; IMMEDIATE hides them.
    const usesReference = openMode === 'REFERENCE';
    // Auto force-close a pair held ≥ N business days. 0 = disabled.
    const [maxHoldingDays, setMaxHoldingDays] = useState<number>(0);
    // Candlestick time scale for the futures chart (same options as the stock
    // chart in grid). CandlestickChart self-fetches /price/chart on change.
    const [chartInterval, setChartInterval] = useState<'1min' | '5min' | '1h' | '1day' | '1w'>('1day');
    // Which chart occupies the top slot. Price is the default — the PnL panes are
    // only useful once there is a position or a reference to measure against.
    const [chartView, setChartView] = useState<'price' | 'pnl'>('price');
    // The guide's spread step: chart view it switched away from, and whether it lent the chart a demo stock.
    const tourChartViewRef = useRef<'price' | 'pnl' | null>(null);
    const [tourDemoStock, setTourDemoStock] = useState(false);
    // The PnL pane's own bucket. Kept separate from `chartInterval` because the
    // two charts can offer different ranges: candles come from the OHLC store,
    // the PnL series only has this session's buffer.
    const [pnlInterval, setPnlInterval] = useState<PnlInterval>('5s');

    // Dual-leg allocator knobs. derivPortfolioId + both leg cash values are required
    // for placement; the place flow refuses to submit without them.
    const [derivPortfolioId, setDerivPortfolioId] = useState<string | undefined>(undefined);
    const [stockAllocatedCash, setStockAllocatedCash] = useState<number | null>(null);
    const [derivAllocatedCash, setDerivAllocatedCash] = useState<number | null>(null);
    const [balanceMode, setBalanceMode] = useState<'NOTIONAL_MATCH' | 'MANUAL'>('NOTIONAL_MATCH');

    // NOTIONAL_MATCH: seed the deriv leg with the MINIMUM cash that backs the stock
    // notional at the broker's floor. It is a floor, not a target — no buffer is
    // added on top, because how much cushion the deriv leg carries is the user's
    // funding decision (Deposit modal), not a multiplier we apply for them.
    useEffect(() => {
        if (balanceMode !== 'NOTIONAL_MATCH') return;
        if (stockAllocatedCash == null) {
            setDerivAllocatedCash(null);
            return;
        }
        setDerivAllocatedCash(Math.round(stockAllocatedCash * DEFAULT_DERIV_MIN_MARGIN_RATE));
    }, [balanceMode, stockAllocatedCash]);

    // Debounce timer refs
    const futureSearchTimerRef = useRef<NodeJS.Timeout | null>(null);

    // `?portfolio=` seeds the first selection only; usePairUrlState mirrors it after that.
    const [initialUrlPortfolio] = useState(() => new URLSearchParams(location.search).get('portfolio'));

    // Portfolio management hook
    const {
        portfolios,
        selectedPortfolioId,
        setSelectedPortfolioId,
        selectedPortfolio,
        loading: loadingPortfolios
    } = usePortfolios({ initialPortfolioId: initialUrlPortfolio });

    const [existingPairConfig, setExistingPairConfig] = useState<any>(null);
    const [loadingPairConfig, setLoadingPairConfig] = useState(false);

    // Pair-level cash-op state (deposit/withdraw/stop bot) — surfaced next to the
    // trading parameters so the operator doesn't have to flip to the Portfolio tab.
    // Capacity drives button enable/disable.
    const [pairCapacity, setPairCapacity] = useState<PairCashCapacity | null>(null);
    const [pairModalMode, setPairModalMode] = useState<'DEPOSIT' | 'WITHDRAW' | null>(null);
    const [pairStopLoading, setPairStopLoading] = useState(false);
    const [pairFlattenLoading, setPairFlattenLoading] = useState(false);
    // Bot Orders panel: today's orders read from the bot's Redis journal.
    const [botOrders, setBotOrders] = useState<PairBotOrder[]>([]);
    const [botOrdersStalled, setBotOrdersStalled] = useState(0);
    const [botOrdersError, setBotOrdersError] = useState<string | null>(null);
    const { modal: appModal, message: appMessage } = App.useApp();

    // Daily schedule for this (portfolio, strategy). placePair auto-creates a 9 AM
    // entry on BE side; surface it here so the operator can see / edit / delete
    // without bouncing to the Executions Management page. Mirrors grid's inline strip.
    interface PairDailySchedule {
        id: string;
        cronDaily: string;
        isActive: boolean;
        updatedAt?: string;
        strategyId?: string;
        portfolioId?: string;
    }
    const [schedule, setSchedule] = useState<PairDailySchedule | null>(null);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [showScheduleForm, setShowScheduleForm] = useState(false);
    const [scheduleTime, setScheduleTime] = useState<dayjs.Dayjs | null>(dayjs('09:00', 'HH:mm'));
    const [scheduleActive, setScheduleActive] = useState(true);
    const [savingSchedule, setSavingSchedule] = useState(false);
    // Start confirmation modal (mirrors grid's "Place Grid — Order Summary"):
    // shows the config summary + schedule editor before the bot actually starts.
    const [startConfirmVisible, setStartConfirmVisible] = useState(false);

    // Pair config loader hook
    const { loadPairConfigIntoForm, loading: loadingConfig } = usePairConfigLoader({
        setTakeProfitReturn,
        setCutLossReturn,
        setIsRunning: setIsStarting,
        setMaxOpen,
        setActiveSide,
        setFutureInstrumentPrice,
        setIsPause,
        setOpenMode,
        setMaxHoldingDays,
        setSelectedStocks,
        setStockProportion,
        setDerivPortfolioId,
        setStockAllocatedCash,
        setDerivAllocatedCash,
        setBalanceMode,
    });

    // Broker purchasing power for the stock leg. Used to clamp the stock-leg cash
    // input and the deposit modal — pair places are dual-leg now, so this is
    // strictly the stock side cap.
    const { purchasingPower } = useBalanceInfo({
        selectedPortfolioId
    });

    // The currently selected portfolio acts as the stock (EQUITY) leg; the
    // deriv-leg dropdown lists DERIVATIVES-typed portfolios from the same
    // user, minus whatever is already picked as the stock leg.
    const equityPortfolios = useMemo(
        () => portfolios.filter(p => p.accountType === 'EQUITY'),
        [portfolios],
    );
    const derivPortfolios = useMemo(
        () => portfolios.filter(p => p.accountType === 'DERIVATIVES' && p.id !== selectedPortfolioId),
        [portfolios, selectedPortfolioId],
    );

    // usePortfolios() auto-selects portfolios[0] which may be a DERIVATIVES
    // sub-account — without correcting, the user would accidentally pick the
    // deriv portfolio as the stock leg and the deriv dropdown would appear
    // empty (filtered out by selectedPortfolioId guard above).
    useEffect(() => {
        if (loadingPortfolios) return;
        const current = portfolios.find(p => p.id === selectedPortfolioId);
        if (current && current.accountType !== 'EQUITY' && equityPortfolios.length > 0) {
            setSelectedPortfolioId(equityPortfolios[0].id);
        }
    }, [loadingPortfolios, portfolios, selectedPortfolioId, equityPortfolios, setSelectedPortfolioId]);
    const derivPortfolio = useMemo(
        () => portfolios.find(p => p.id === derivPortfolioId),
        [portfolios, derivPortfolioId],
    );

    // --- Which pair is on screen -------------------------------------------
    // A portfolio holds many pair configs (drafts, the live one, history). The URL
    // names the selected one and every panel and action below follows it.
    const pairList = usePairList(selectedPortfolioId || null);
    const refreshPairList = pairList.refresh;
    const equityPortfolioIds = useMemo(() => equityPortfolios.map((p) => p.id), [equityPortfolios]);
    const { selectedKey: selectedPairKey, selectPair, clearPair } = usePairUrlState({
        portfoliosLoaded: !loadingPortfolios,
        portfolioIds: equityPortfolioIds,
        selectedPortfolioId,
        setSelectedPortfolioId,
        pairs: pairList.pairs,
        currentDataConfigId: pairList.currentDataConfigId,
    });
    const isNewPair = selectedPairKey === NEW_PAIR_KEY;
    const selectedPair = useMemo(
        () => pairList.pairs?.find((p) => p.dataConfigId === selectedPairKey) ?? null,
        [pairList.pairs, selectedPairKey],
    );
    const pairDataConfigId = selectedPairKey && !isNewPair ? selectedPairKey : null;
    // ENDING / ENDED: nothing on this pair may change any more.
    const pairReadOnly = isPairReadOnly(selectedPair?.lifecycleStatus);
    // Import is the way out of a stuck ENDING, so only an ENDED pair locks it.
    const pairImportLocked = selectedPair?.lifecycleStatus === 'ENDED';
    // Accounting, reference PnL and bot phase describe the engine, i.e. the live pair only.
    const selectedIsLive = !!selectedPair?.live;
    const [newPairName, setNewPairName] = useState('');

    // --- Settings change detection -----------------------------------------
    // Pair had no dirty tracking, so the Save button nagged on every place even
    // when nothing changed. Build a signature of exactly the fields that Save
    // persists (DataConfig.configData + per-stock proportions + instruments),
    // and compare it to the last loaded/saved baseline. Allocation cash is
    // intentionally excluded — it changes via deposit/withdraw, not Save.
    const settingsSignature = useMemo(() => JSON.stringify({
        closeType,
        takeProfitReturn,
        cutLossReturn,
        maxOpen,
        activeSide,
        futureInstrumentPrice: futureInstrumentPrice ?? null,
        isPause,
        openMode,
        maxHoldingDays,
        stockProportion,
        derivPortfolioId: derivPortfolioId ?? null,
        balanceMode,
        // NOTE: selectedFuture intentionally excluded. The config loader never
        // restores a per-config future — selectedFuture is set asynchronously by
        // useDefaultFuturePrice (always the default), arriving after the baseline
        // is captured. Including it would falsely mark settingsDirty on every load.
        // Stock signature includes ref price + flash limit so editing those marks dirty.
        stocks: [...selectedStocks]
            .map((s) => `${(s as any).exchange ?? ''}:${s.symbol}@${s.proportion ?? ''}#${s.referencePrice ?? ''}#${s.flashVolumeLimit ?? ''}`)
            .sort(),
    }), [closeType, takeProfitReturn, cutLossReturn, maxOpen,
        activeSide, futureInstrumentPrice, isPause, openMode, maxHoldingDays, stockProportion, derivPortfolioId, balanceMode,
        selectedStocks]);

    const signatureRef = useRef(settingsSignature);
    useEffect(() => { signatureRef.current = settingsSignature; }, [settingsSignature]);

    /** Put a whole form state on screen (pair switch, draft restore, reset). */
    const applyFormSnapshot = React.useCallback((snap: PairFormSnapshot) => {
        setTakeProfitReturn(snap.takeProfitReturn);
        setCutLossReturn(snap.cutLossReturn);
        setMaxOpen(snap.maxOpen);
        setActiveSide(snap.activeSide);
        setFutureInstrumentPrice(snap.futureInstrumentPrice);
        setIsPause(snap.isPause);
        // A draft stored before the mode existed belongs to a reference-trigger form.
        setOpenMode(snap.openMode ?? 'REFERENCE');
        setMaxHoldingDays(snap.maxHoldingDays);
        setStockProportion(snap.stockProportion);
        setDerivPortfolioId(snap.derivPortfolioId);
        setStockAllocatedCash(snap.stockAllocatedCash);
        setDerivAllocatedCash(snap.derivAllocatedCash);
        setBalanceMode(snap.balanceMode);
        setSelectedStocks(snap.selectedStocks);
    }, []);
    const captureFormSnapshot = React.useCallback((): PairFormSnapshot => ({
        takeProfitReturn, cutLossReturn, maxOpen, activeSide, futureInstrumentPrice, isPause, openMode,
        maxHoldingDays, stockProportion, derivPortfolioId, stockAllocatedCash, derivAllocatedCash,
        balanceMode, selectedStocks,
    }), [takeProfitReturn, cutLossReturn, maxOpen, activeSide, futureInstrumentPrice, isPause, openMode,
        maxHoldingDays, stockProportion, derivPortfolioId, stockAllocatedCash, derivAllocatedCash,
        balanceMode, selectedStocks]);

    // Baseline = signature at last load/save. `null` = nothing loaded yet.
    const [savedSignature, setSavedSignature] = useState<string | null>(null);
    const [settingsCaptureToken, setSettingsCaptureToken] = useState(0);
    // Call after a config is loaded OR saved to re-baseline "current = clean".
    const markSettingsSaved = React.useCallback(() => setSettingsCaptureToken((t) => t + 1), []);
    // A pair switch baselines the loaded form, THEN lays the tab's unsaved draft on top
    // and only then marks the form as belonging to that tab (so drafts sync from there).
    const pendingFormKeyRef = useRef<string | null>(null);
    const pendingDraftRef = useRef<PairFormSnapshot | null>(null);
    const [formReadyKey, setFormReadyKey] = useState<string | null>(null);
    useEffect(() => {
        // Runs after the load setters have committed (signatureRef effect above
        // is declared first, so it updates the ref before this reads it). On a
        // pure save the signature is unchanged but the ref already holds it.
        setSavedSignature(signatureRef.current);
        if (pendingFormKeyRef.current !== null) {
            if (pendingDraftRef.current) applyFormSnapshot(pendingDraftRef.current);
            pendingDraftRef.current = null;
            setFormReadyKey(pendingFormKeyRef.current);
            pendingFormKeyRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsCaptureToken]);

    // Dirty when there's no baseline yet (must Save to create) or the form drifted.
    const settingsDirty = savedSignature === null || savedSignature !== settingsSignature;

    // A SECOND, narrower signature: only the inputs the OPEN trigger reads.
    // The engine's published PnL is computed from the SAVED reference prices, so
    // the moment the operator edits one of these the engine's number answers a
    // different question than the one being asked on screen — and typing a
    // reference price has to move the number, or the field is unusable. Scoped
    // tighter than settingsSignature on purpose: editing max_open or the TP/CL
    // returns must NOT switch the display away from the engine.
    const referenceSignature = useMemo(() => JSON.stringify({
        activeSide,
        futureInstrumentPrice: futureInstrumentPrice ?? null,
        stocks: [...selectedStocks]
            .map((st) => `${st.symbol}@${st.proportion ?? ''}#${st.referencePrice ?? ''}`)
            .sort(),
    }), [activeSide, futureInstrumentPrice, selectedStocks]);

    const referenceSignatureRef = useRef(referenceSignature);
    useEffect(() => { referenceSignatureRef.current = referenceSignature; }, [referenceSignature]);
    const [savedReferenceSignature, setSavedReferenceSignature] = useState<string | null>(null);
    useEffect(() => {
        setSavedReferenceSignature(referenceSignatureRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsCaptureToken]);
    // Re-baselined by the same token as settingsDirty (load / save), so a
    // successful Save hands the display straight back to the engine.
    const referenceInputsDirty =
        savedReferenceSignature === null || savedReferenceSignature !== referenceSignature;

    // --- Per-tab drafts -----------------------------------------------------
    // Unsaved edits are kept per pair tab (dc id or `new`) in memory and mirrored to
    // sessionStorage, so switching tabs or reloading never drops them.
    const draftsRef = useRef<Map<string, PairFormSnapshot>>(new Map());
    const [draftsVersion, setDraftsVersion] = useState(0);
    const persistDrafts = React.useCallback(() => {
        if (selectedPortfolioId) savePairDrafts(selectedPortfolioId, draftsRef.current);
        setDraftsVersion((v) => v + 1);
    }, [selectedPortfolioId]);
    const [newTabRequested, setNewTabRequested] = useState(false);
    useEffect(() => {
        draftsRef.current = selectedPortfolioId ? loadPairDrafts(selectedPortfolioId) : new Map();
        setNewTabRequested(false);
        setDraftsVersion((v) => v + 1);
    }, [selectedPortfolioId]);

    // Load the selected pair into the form. GETs only — switching never mutates anything.
    const loadSeqRef = useRef(0);
    useEffect(() => {
        if (!selectedPortfolioId || !selectedPairKey) return;
        const seq = ++loadSeqRef.current;
        const isCurrent = () => loadSeqRef.current === seq;
        setFormReadyKey(null);
        setExistingPairConfig(null);
        // An ended pair shows what it ran with, never an edit left over from before it ended.
        const draft = pairReadOnly ? null : draftsRef.current.get(selectedPairKey) ?? null;

        if (selectedPairKey === NEW_PAIR_KEY) {
            // Empty form; the deriv account is wiring, not config, so the only one is preset.
            applyFormSnapshot({
                ...DEFAULT_PAIR_FORM,
                derivPortfolioId: derivPortfolios.length === 1 ? derivPortfolios[0].id : undefined,
            });
            setNewPairName(draft?.name ?? '');
            pendingDraftRef.current = draft;
            pendingFormKeyRef.current = NEW_PAIR_KEY;
            markSettingsSaved();
            return;
        }

        applyFormSnapshot(DEFAULT_PAIR_FORM);
        (async () => {
            setLoadingPairConfig(true);
            try {
                const token = localStorage.getItem('access_token');
                const res = await coreService.get(`/pair/settings/${selectedPortfolioId}`, {
                    params: { dataConfigId: selectedPairKey },
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                });
                if (!isCurrent()) return;
                if (res?.data) {
                    setExistingPairConfig(res.data);
                    // Must await: the loader refetches each stock before setting them, and
                    // baselining earlier would mark the real values as unsaved edits.
                    await loadPairConfigIntoForm(res.data, isCurrent);
                }
            } catch (error: any) {
                if (isCurrent() && error?.response?.status !== 404) {
                    console.error('Error fetching pair configuration:', error);
                }
            } finally {
                if (isCurrent()) setLoadingPairConfig(false);
            }
            if (!isCurrent()) return;
            pendingDraftRef.current = draft;
            pendingFormKeyRef.current = selectedPairKey;
            markSettingsSaved();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPortfolioId, selectedPairKey]);

    // Edits on the tab that owns the form become (or stop being) its draft.
    const formHasUnsavedEdits = savedSignature !== null && savedSignature !== settingsSignature;
    useEffect(() => {
        if (!selectedPortfolioId || !formReadyKey || formReadyKey !== selectedPairKey) return;
        const isNewTab = formReadyKey === NEW_PAIR_KEY;
        const drafts = draftsRef.current;
        const had = drafts.has(formReadyKey);
        if (!pairReadOnly && (formHasUnsavedEdits || (isNewTab && newPairName.trim() !== ''))) {
            drafts.set(formReadyKey, { ...captureFormSnapshot(), ...(isNewTab ? { name: newPairName } : {}) });
        } else if (had) {
            drafts.delete(formReadyKey);
        } else {
            return;
        }
        persistDrafts();
    }, [selectedPortfolioId, formReadyKey, selectedPairKey, pairReadOnly, formHasUnsavedEdits,
        newPairName, captureFormSnapshot, persistDrafts]);

    const pairDirtyKeys = useMemo(
        () => new Set(draftsRef.current.keys()),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [draftsVersion],
    );
    const newTabOpen = isNewPair || newTabRequested || pairDirtyKeys.has(NEW_PAIR_KEY);

    // Place pair strategy hook
    const {
        placingPair,
        placePairModalVisible,
        placePairResult,
        handlePlacePairStrategy,
        handleClosePlacePairModal,
        savingSettings,
        handleSavePairSettings,
    } = usePlacePairStrategy({
        selectedFuture,
        selectedStocks,
        selectedPortfolio,
        selectedPortfolioId,
        closeType,
        takeProfitReturn,
        cutLossReturn,
        maxOpen,
        activeSide,
        futureInstrumentPrice,
        isPause,
        openMode,
        maxHoldingDays,
        executionId,
        strategyId,
        onConfigUpdated: (config) => {
            setExistingPairConfig(config);
            markSettingsSaved(); // just persisted → current form is the new clean baseline
        },
        // Dual-leg + allocator knobs. All four (deriv portfolio + both leg cash
        // values + selected future) are required at submit time.
        derivPortfolio,
        stockAllocatedCash: stockAllocatedCash ?? undefined,
        derivAllocatedCash: derivAllocatedCash ?? undefined,
        balanceMode,
        dataConfigId: pairDataConfigId,
        createNew: isNewPair,
        pairName: newPairName,
    });

    // --- Pair tabs ----------------------------------------------------------
    const forgetNewPairTab = React.useCallback(() => {
        draftsRef.current.delete(NEW_PAIR_KEY);
        persistDrafts();
        setNewTabRequested(false);
        setNewPairName('');
    }, [persistDrafts]);

    const handleNewPair = React.useCallback(() => {
        setNewTabRequested(true);
        selectPair(NEW_PAIR_KEY);
    }, [selectPair]);

    const handleCloseNewPair = React.useCallback(() => {
        const close = () => {
            // Detach the form first so the draft sync cannot write the draft back.
            if (isNewPair) setFormReadyKey(null);
            forgetNewPairTab();
            if (isNewPair) clearPair();
        };
        if (!draftsRef.current.has(NEW_PAIR_KEY)) {
            close();
            return;
        }
        appModal.confirm({
            title: 'Discard the new pair?',
            content: 'Its settings have not been saved and will be lost.',
            okText: 'Discard',
            okButtonProps: { danger: true },
            cancelText: 'Keep editing',
            onOk: close,
        });
    }, [isNewPair, forgetNewPairTab, clearPair, appModal]);

    // Prefill a New pair tab from the one on screen; reference prices stay empty.
    const handleCopyToNewPair = React.useCallback(() => {
        const snapshot = copySnapshotForNewPair(captureFormSnapshot());
        const open = () => {
            draftsRef.current.set(NEW_PAIR_KEY, snapshot);
            persistDrafts();
            setNewPairName('');
            setNewTabRequested(true);
            selectPair(NEW_PAIR_KEY);
        };
        if (!draftsRef.current.has(NEW_PAIR_KEY)) {
            open();
            return;
        }
        appModal.confirm({
            title: 'Replace the unsaved new pair?',
            content: 'You already have an unsaved New pair tab. Copying replaces its settings.',
            okText: 'Replace',
            cancelText: 'Cancel',
            onOk: open,
        });
    }, [captureFormSnapshot, persistDrafts, selectPair, appModal]);

    const handlePairEnded = React.useCallback((wasDraft: boolean) => {
        void (async () => {
            await refreshPairList();
            // A discarded draft has nothing worth showing; an ended pair shows its summary.
            if (wasDraft) clearPair();
        })();
    }, [refreshPairList, clearPair]);

    // Saving the New pair created a draft: drop the client tab and open the real one.
    const handledPlaceResultRef = useRef<unknown>(null);
    useEffect(() => {
        if (!placePairResult?.success || handledPlaceResultRef.current === placePairResult) return;
        handledPlaceResultRef.current = placePairResult;
        const createdId = placePairResult.data?.dataConfigId;
        if (!isNewPair || !createdId) return;
        setFormReadyKey(null);
        forgetNewPairTab();
        void (async () => {
            // The list must know the id first, or the URL would fall back to another pair.
            await refreshPairList();
            selectPair(createdId, { replace: true });
        })();
    }, [placePairResult, isNewPair, forgetNewPairTab, refreshPairList, selectPair]);

    // Stock search hook
    const {
        searchResults: stockSearchResults,
        loading: stockSearchLoading,
        handleSearch: handleStockSearch,
        clearResults: clearStockSearchResults
    } = useStockSearch();

    // Load default data on mount - automatically select the first future symbol
    useDefaultFuturePrice({ setSelectedFuture });

    // The contract the pair's futures leg actually trades. `deriv_symbol` comes
    // from the persisted InstrumentConfig, so it follows an expiry-day roll
    // (VN30F2608 → VN30F2609); `selectedFuture` is only the chart's front-month
    // default and lags on the roll day itself.
    const futureLegSymbol =
        (existingPairConfig?.deriv_symbol as string | undefined)?.split(':').pop() ||
        selectedFuture?.symbol ||
        null;
    // SELL side = short the future, long the stocks.
    const futureLegIsShort = activeSide === 'SELL';

    // Live prices for every instrument the two legs actually trade: the futures
    // contract plus each selected stock. One subscription set feeds both the
    // Market cells and the per-leg reference PnL, so the tables tick with the
    // market instead of freezing on the snapshot taken when the config loaded.
    // Roll modal. `rollTargetSymbol` is subscribed for prices while the modal is
    // open so the operator can read the NEW contract's live price before naming
    // its target — the old contract's price is frozen and useless for that.
    const [rollModalOpen, setRollModalOpen] = useState(false);
    const [rollTargetSymbol, setRollTargetSymbol] = useState<string | null>(null);
    // Futures-roll plan: when this pair's deriv leg rolls, the expiry countdown,
    // and the orphan diagnosis when nobody rolled in time. Server-resolved — the
    // browser has no holiday calendar and must not guess an expiry date.
    const [rollPlanModalOpen, setRollPlanModalOpen] = useState(false);
    const [pairImportOpen, setPairImportOpen] = useState(false);
    const rollPlan = usePairRollPlan(pairReadOnly ? null : pairDataConfigId);

    // What the CHART and the BASIS show. Same question as `futureLegSymbol` but sourced
    // from the roll status too, so the page follows a roll the moment it completes —
    // `selectedFuture` is the front-month from the instrument search, and the roll runs
    // on the session BEFORE expiry, so it names the wrong contract for a whole day.
    const chartContract = useMemo(
        () =>
            resolveChartContract({
                rollStatus: rollPlan.status,
                configSymbol: existingPairConfig?.deriv_symbol as string | undefined,
                defaultSymbol: selectedFuture?.symbol,
            }),
        [rollPlan.status, existingPairConfig?.deriv_symbol, selectedFuture?.symbol],
    );

    // The chart switching contract under the operator is exactly the kind of silent
    // change that costs an afternoon of confusion, so it is said out loud.
    const chartContractLine = useMemo(
        () => chartContractNote({ contract: chartContract, defaultSymbol: selectedFuture?.symbol }),
        [chartContract, selectedFuture?.symbol],
    );

    // The chart's basket: the form's, or the guide's demo stock while the form has none.
    const chartStocks = useMemo<StockInstrument[]>(
        () => (tourDemoStock && !selectedStocks.some((s) => (s.proportion ?? 0) > 0)
            ? [{ symbol: PAIR_ONBOARDING_DEMO_STOCK, name: PAIR_ONBOARDING_DEMO_STOCK, price: 0, volume: 0, marketCap: 0, change24h: 0, changePercent24h: 0, proportion: 1 }]
            : selectedStocks),
        [tourDemoStock, selectedStocks],
    );
    const livePriceSymbols = useMemo(
        () => [
            futureLegSymbol,
            // Usually the same contract; differs for the day the engine has rolled and
            // the config has not, and the chart needs a live price either way.
            chartContract.symbol,
            rollTargetSymbol,
            ...chartStocks.map((s) => s.symbol),
        ],
        [futureLegSymbol, chartContract.symbol, rollTargetSymbol, chartStocks],
    );
    const livePrices = useLivePrices(livePriceSymbols);

    // The leg's own contract is streamed unconditionally above, so this no longer
    // rides on the chart's subscription — `futurePriceData` follows
    // `selectedFuture`, the front-month default, which names a different contract
    // than the leg after an expiry-day roll. Chart feed / snapshot stay as
    // fallbacks for the gap before the first tick.
    const futureLegMarketPrice =
        livePriceOf(livePrices, futureLegSymbol) ??
        futurePriceData?.latest_matched_price ??
        selectedFuture?.price ??
        null;

    // Contracts currently short on the futures leg, summed across open pairs.
    const futureLegQty = useMemo(
        () =>
            pairPositions.reduce(
                (total, pair) =>
                    total +
                    pair.legs
                        .filter((l) => l.instrument.startsWith('HNXDS:') && l.side === 'SHORT')
                        .reduce((sum, l) => sum + l.quantity, 0),
                0,
            ),
        [pairPositions],
    );

    // The ENGINE's own OPEN-trigger breakdown, per leg, PER PAIR (one contract).
    // Read, never recomputed: this is the arithmetic the bot actually decided on.
    // `reporting: false` ⇒ no engine has reported recently (bot stopped) and the
    // local preview below takes over.
    const { data: enginePublishedRaw } = usePairReferencePnl(selectedIsLive ? selectedPortfolioId : null);
    const enginePublished = selectedIsLive ? enginePublishedRaw : null;
    // Suppressed while the trigger inputs carry unsaved edits: the engine is
    // still reporting on the SAVED reference, and showing that next to a number
    // the operator just typed reads as "your input did nothing".
    const engineReferencePnl = referenceInputsDirty ? null : enginePublished;
    const engineStockLegs = useMemo(
        () => engineStocksByTicker(engineReferencePnl),
        [engineReferencePnl],
    );
    // An expired contract stops quoting, so its Market Price freezes at the last
    // value the REST seed returned and the futures leg of the reference PnL can
    // never move again. On screen that is indistinguishable from a quiet market,
    // so say it outright — the fix is to roll the deriv leg's symbol.
    const futureLegExpiry = useMemo(() => {
        if (!futureLegSymbol || !isExpiredContract(futureLegSymbol)) return null;
        const expiry = futureExpiry(futureLegSymbol);
        return {
            on: expiry ? formatExpiry(expiry) : null,
            rollTo: nextContract(futureLegSymbol),
        };
    }, [futureLegSymbol]);

    // Why the ROLL action is (not) offered. See manualRollBlock: only an expired
    // contract earns it — while the leg still trades, a roll walks off the liquid
    // front month AND discards the reference price, and the two things people
    // actually want there have their own paths.
    // The server's expiry wins when we have it: it is holiday-aware, and on a
    // day a holiday moved the expiry the local third-Thursday rule would call a
    // settled contract live and hide the button that fixes it.
    const rollBlock = useMemo(
        () => manualRollBlock(
            futureLegSymbol,
            Boolean(pairDataConfigId),
            new Date(),
            rollPlan.status?.plan.expiry ?? null,
        ),
        [futureLegSymbol, pairDataConfigId, rollPlan.status],
    );
    const rollTooltip = useMemo(() => {
        if (!rollBlock) {
            return 'Move the futures leg to the next contract and set its target price. The pair must be stopped and the leg flat.';
        }
        if (rollBlock.reason === 'no_pair') {
            return 'Save the pair first — there is no deriv leg to roll yet.';
        }
        const expiry = rollBlock.expiry ? formatExpiry(rollBlock.expiry) : null;
        return (
            `${rollBlock.symbol || 'This contract'} still trades` +
            `${expiry ? ` (expires ${expiry})` : ''}, so there is nothing to roll. ` +
            'The bot rolls itself on the roll date in Roll settings (by default the '
            + 'trading day before expiry, at 14:00). To change only the target ' +
            'price, edit it here and Save — it gets re-stamped against the current contract.'
        );
    }, [rollBlock]);

    const referencePnlFrozenNote = futureLegExpiry
        ? `${futureLegSymbol} expired${futureLegExpiry.on ? ` on ${futureLegExpiry.on}` : ''} and no longer quotes — ` +
          `the futures side of this number cannot move. Roll the deriv leg` +
          `${futureLegExpiry.rollTo ? ` to ${futureLegExpiry.rollTo}` : ''} and re-enter its target price.`
        : null;

    // The engine trades whatever contract IT resolved; the futures row is
    // labelled from the persisted deriv_symbol. They diverge when an expiry-day
    // roll could not report itself to the BE ([[pair-futures-autoroll]]), and a
    // PnL shown under the wrong ticker is worse than no PnL.
    const engineFutureMismatch = useMemo(() => {
        const engineContract = engineReferencePnl?.future?.instrument?.split(':').pop();
        if (!engineContract || !futureLegSymbol) return undefined;
        return engineContract === futureLegSymbol
            ? undefined
            : `the bot is on ${engineContract}, this row is labelled ${futureLegSymbol}`;
    }, [engineReferencePnl, futureLegSymbol]);

    // `future_instrument_price_symbol` describes the PERSISTED reference. Once the
    // operator edits the price in the form the stamp no longer describes what they
    // typed, so gating on it would refuse a perfectly good fresh value entered for
    // the current contract — the exact repair an operator does after a roll. A
    // mistyped fresh value is still caught by the 5% drift check.
    const stampedReferenceSymbol = useMemo(() => {
        const persisted = existingPairConfig?.future_instrument_price;
        if (persisted == null || futureInstrumentPrice == null) return null;
        return Number(persisted) === Number(futureInstrumentPrice)
            ? ((existingPairConfig?.future_instrument_price_symbol as string | undefined) ?? null)
            : null;
    }, [existingPairConfig, futureInstrumentPrice]);

    // Client-side PREVIEW of the same trigger: what the values currently in the
    // form would produce. This is what the operator sees before Start, and also
    // whenever they are mid-edit on a running pair. Same one-contract scope and
    // the same stale-reference refusal as the engine, so the two can't tell
    // different stories about the same inputs.
    const previewBreakdown = useMemo(
        () =>
            computePairReferencePnl({
                futureReferencePrice: futureInstrumentPrice,
                futureMarketPrice: futureLegMarketPrice,
                referenceSymbol: stampedReferenceSymbol,
                futureLegSymbol,
                activeSide,
                stocks: selectedStocks.map((stock) => ({
                    symbol: stock.symbol,
                    proportion: stock.proportion,
                    referencePrice: stock.referencePrice,
                    // Live tick, or the load-time snapshot until one arrives.
                    marketPrice: livePriceOf(livePrices, stock.symbol) ?? stock.price,
                })),
            }),
        [
            futureInstrumentPrice,
            futureLegMarketPrice,
            stampedReferenceSymbol,
            futureLegSymbol,
            activeSide,
            selectedStocks,
            livePrices,
        ],
    );

    // What the banner shows: the engine's total when it reports AND the form
    // agrees with what was saved; otherwise the preview. `source` drives the
    // label — the two mean different things (decided vs hypothetical) and must
    // never be shown interchangeably.
    const referencePnl = useMemo(
        () => resolveReferenceSource(enginePublished, previewBreakdown, referenceInputsDirty),
        [enginePublished, previewBreakdown, referenceInputsDirty],
    );

    // Card next to Basis: unrealized PnL of the open pairs, straight from the
    // accounting endpoint that also backs the Position tab, so the two always
    // agree. It used to be lifted out of the (compact, so book-only) BidAsk in
    // the Order Book tab, which sourced a different endpoint, scaled it by 1000,
    // and silently switched to REALIZED whenever that hidden component's own
    // open/closed filter moved.
    // Null when any pair has a leg with no price: a partial sum would read as a real figure.
    const totalPnl = useMemo(
        () => pairPositions.some((pair) => pair.totalPnl == null)
            ? null
            : pairPositions.reduce((sum, pair) => sum + (pair.totalPnl ?? 0), 0),
        [pairPositions],
    );

    // Unrealized PnL of the positions actually on the book, per ticker, marked to
    // the live price. Outranks both reference sources in the per-row columns:
    // a filled leg shows real money, not a hypothetical entry.
    const positionPnl = useMemo(
        () => computePositionPnl(pairPositions, (ticker) => livePriceOf(livePrices, ticker)),
        [pairPositions, livePrices],
    );

    // Per-pair PnL and return for the PnL chart. The margin FLOOR comes from the
    // allocator (broker registry, keyed by brokerCode) rather than a constant, so
    // the denominator matches the engine's own even if a broker's rate changes.
    const { data: allocationBalance } = usePairAllocationBalance(pairDataConfigId);
    const pairMetrics = useMemo(
        () => computePairMetrics(
            pairPositions,
            (ticker) => livePriceOf(livePrices, ticker),
            allocationBalance?.derivLeg?.minMarginRate ?? DEFAULT_DERIV_MIN_MARGIN_RATE,
            allocationBalance?.derivLeg?.multiplier ?? DEFAULT_DERIV_MULTIPLIER,
        ),
        [pairPositions, livePrices, allocationBalance],
    );
    // Percent for the same card. Book-wide: Σ PnL / Σ capital tied up — the same
    // denominator the engine's own TP/CL uses per pair (stock at full cash,
    // futures at the margin floor), so the number is comparable to the TP/SL the
    // operator typed even though the exits themselves are per pair.
    const totalReturnPct = useMemo(() => aggregateReturnPct(pairMetrics), [pairMetrics]);

    // The spread chart: one contract against a basket fixed at the previous
    // session's close (see utils/pairSpreadHistory). Independent of positions and of
    // the reference prices, so it always means the same thing.
    const spreadSymbols = useMemo(
        () => chartStocks.map((s) => ({ symbol: s.symbol, proportion: s.proportion })),
        [chartStocks],
    );
    const { notional: notionalHistory, marks: spreadMarks } = usePairSpreadHistory({
        futureSymbol: futureLegSymbol,
        stocks: spreadSymbols,
        multiplier: allocationBalance?.derivLeg?.multiplier ?? DEFAULT_DERIV_MULTIPLIER,
        interval: pnlInterval,
    });

    const legNotional = useMemo(() => {
        const multiplier = allocationBalance?.derivLeg?.multiplier ?? DEFAULT_DERIV_MULTIPLIER;
        const basket = spreadBasket(spreadMarks, spreadSymbols, multiplier);
        const notional = computeLegNotional({
            futPrice: futureLegMarketPrice,
            multiplier,
            contracts: 1,
            stocks: chartStocks.map((inst) => ({
                quantity: basket?.[inst.symbol] ?? 0,
                // `inst.price` is the snapshot taken when the config loaded — a real
                // price for this symbol, just older than a tick.
                price: livePriceOf(livePrices, inst.symbol) ?? inst.price ?? null,
                unit: 1000,
            })),
        });
        // No previous close for some leg ⇒ no basket, not an empty one worth 0.
        return basket ? notional : { ...notional, equityValue: null };
    }, [livePrices, futureLegMarketPrice, allocationBalance, chartStocks, spreadSymbols, spreadMarks]);
    // The readout card shows the chart's own number, so the two cannot disagree.
    const spreadPct = spreadPercent(legNotional.futValue, legNotional.equityValue);

    // Buffered in the browser from the page opening; the candle history covers
    // what came before.
    // Paused while the guide lends a demo stock, so its basket never lands in the buffer.
    const pnlHistory = usePnlHistory({
        futValue: legNotional.futValue,
        equityValue: legNotional.equityValue,
    }, !tourDemoStock);

    // priceData for the future chart header (Ref/CE/FL strip, like the stock
    // side). Keep the live feed's daily band, but fall back to the user's
    // configured reference price for "Ref" when the feed hasn't sent a daily
    // ref yet, so the trigger anchor is always visible.
    const futureHeaderData = useMemo(() => {
        if (!futurePriceData) return null;
        return {
            ...futurePriceData,
            ref_price: futurePriceData.ref_price ?? futureInstrumentPrice ?? undefined,
        };
    }, [futurePriceData, futureInstrumentPrice]);

    // Check if user has portfolios, redirect to register if not
    useEffect(() => {
        if (!loadingPortfolios && portfolios.length === 0) {
            navigate('/register-portfolio');
        }
    }, [portfolios, loadingPortfolios, navigate]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (futureSearchTimerRef.current) {
                clearTimeout(futureSearchTimerRef.current);
            }
        };
    }, []);

    // Select stock
    const handleSelectStock = (value: string) => {
        const stock = stockSearchResults.find(s => s.symbol === value);
        if (stock && !selectedStocks.find(s => s.symbol === stock.symbol)) {
            setSelectedStocks([...selectedStocks, stock]);
            clearStockSearchResults();
        }
    };

    // Remove stock
    const handleRemoveStock = (symbol: string) => {
        setSelectedStocks(selectedStocks.filter(s => s.symbol !== symbol));
    };

    /**
     * Inline edits from the equity table — the fields the proportion modal used to own,
     * typed straight into the row instead.
     *
     * The modal's guard comes with them: the basket budget is 1.0, so a proportion is
     * clamped to whatever the OTHER symbols leave free. That rule used to be enforced by
     * disabling the modal's OK button, and it now has to hold at the keystroke.
     */
    const handleInlineReferencePrice = (symbol: string, value: number | null) => {
        setSelectedStocks(prev => prev.map(s => (
            s.symbol === symbol ? { ...s, referencePrice: value } : s
        )));
    };

    const proportionHeadroomFor = (symbol: string) => {
        const others = selectedStocks.reduce(
            (sum, s) => (s.symbol === symbol ? sum : sum + (s.proportion || 0)),
            0,
        );
        // 0.1 steps summed in binary give totals like 0.30000000000000004, which would
        // leave a headroom of 0.6999999999999999 and clamp a legitimate 0.7 down.
        return Math.max(0, Math.round((1 - others) * 10) / 10);
    };

    const handleInlineFlashLimit = (symbol: string, value: number | null) => {
        setSelectedStocks(prev => prev.map(s => (
            s.symbol === symbol ? { ...s, flashVolumeLimit: value } : s
        )));
    };

    const handleInlineProportion = (symbol: string, value: number | null) => {
        const wanted = value ?? 0;
        const headroom = proportionHeadroomFor(symbol);
        const clamped = Math.min(wanted, headroom);
        if (clamped < wanted) {
            appMessage.warning(
                `Total weight cannot exceed 1.0 — ${symbol} capped at ${formatDecimal(clamped, 1)}.`,
            );
        }
        setSelectedStocks(prev => prev.map(s => (
            s.symbol === symbol ? { ...s, proportion: clamped } : s
        )));
    };

    // Calculate total proportion
    const totalProportion = useMemo(() => {
        return selectedStocks.reduce((sum, stock) => sum + (stock.proportion || 0), 0);
    }, [selectedStocks]);

    // Capacity of the SELECTED pair for the cash-op buttons in the Trading Parameters
    // card. Soft-fail — buttons just stay disabled.
    const capacityForRef = useRef<string | null>(null);
    const fetchPairCapacity = React.useCallback(async () => {
        capacityForRef.current = pairDataConfigId;
        if (!pairDataConfigId) {
            setPairCapacity(null);
            return;
        }
        try {
            const cap = await pairTradingApi.pairCashCapacity(pairDataConfigId);
            if (capacityForRef.current === pairDataConfigId) setPairCapacity(cap);
        } catch {
            if (capacityForRef.current === pairDataConfigId) setPairCapacity(null);
        }
    }, [pairDataConfigId]);

    useEffect(() => {
        setPairCapacity(null);
        void fetchPairCapacity();
    }, [fetchPairCapacity]);

    // After any action: capacity, plus the pair list (status, live/running, PnL).
    const fetchPairCashState = React.useCallback(async () => {
        void refreshPairList();
        await fetchPairCapacity();
    }, [refreshPairList, fetchPairCapacity]);

    // Per-instrument open positions (both legs) for the Position sub-tab under the
    // chart. Soft-fail: empty list on any error (e.g. no open pair book).
    // The panel polls every 20s. Two things used to make it flash on every poll:
    // the blocking "Loading..." replaced the whole list, and a transient error
    // cleared it to "No positions". Neither is a real state change — the book is
    // still whatever it was — so a refresh now keeps the rows and only spins a
    // small marker in the pair header. `positionsLoadedRef` (not the state) so the
    // callback identity stays stable and the 20s timer is not restarted on data.
    const positionsLoadedRef = useRef(false);
    const [refreshingPositions, setRefreshingPositions] = useState(false);
    const [positionsStale, setPositionsStale] = useState(false);
    const fetchPositions = React.useCallback(async () => {
        // The accounting endpoint reads the engine's book, which belongs to the live pair.
        if (!selectedPortfolioId || !selectedIsLive) {
            positionsLoadedRef.current = false;
            setPairPositions([]);
            return;
        }
        const first = !positionsLoadedRef.current;
        if (first) setLoadingPositions(true);
        else setRefreshingPositions(true);
        try {
            const res = await pairTradingApi.getAccounting(selectedPortfolioId);
            setPairPositions(res.pairs ?? []);
            setPendingImport(res.pendingImport ?? null);
            positionsLoadedRef.current = true;
            setPositionsStale(false);
        } catch {
            // Keep the last good rows. Clearing them says "the book is empty",
            // which is a claim a failed request cannot make.
            if (first) setPairPositions([]);
            setPositionsStale(true);
        } finally {
            setLoadingPositions(false);
            setRefreshingPositions(false);
        }
    }, [selectedPortfolioId, selectedIsLive]);

    // Refresh positions when the Position sub-tab becomes active.
    useEffect(() => {
        if (bottomTab === 'position') void fetchPositions();
    }, [bottomTab, fetchPositions]);

    // The per-symbol PnL columns in the config panel are marked against these
    // positions, so they can no longer wait for the Position sub-tab to be
    // opened. Poll slowly: quantity and average cost only change on a fill —
    // between fills it is the live price that moves the number.
    useEffect(() => {
        if (!selectedPortfolioId) return;
        void fetchPositions();
        const timer = setInterval(() => void fetchPositions(), 20000);
        return () => clearInterval(timer);
    }, [selectedPortfolioId, fetchPositions]);

    // Bot Orders: what the bot itself thinks it has working right now. Polled
    // fast while the bot runs (an order stuck unacked is the thing worth
    // catching within seconds), slowly otherwise so a stopped page is quiet.
    // Polled from the server: `pairCapacity` is fetched once per action, so after a Stop
    // (STOPPING until the worker finishes) it stayed "active" and locked every button until
    // a reload. The capacity flag is only the fallback before the first read lands.
    const { phase: pairPhase, refresh: refreshPairPhase } = useExecutionStatus({
        portfolioId: selectedPortfolioId,
        strategyType: 'PAIR_TRADING',
    });
    // The worker is per account and runs the live pair; any other pair is not "running".
    const pairBotActive = selectedIsLive
        && (pairPhase === 'unknown' ? !!pairCapacity?.pairBotActive : isBotUp(pairPhase));

    // Bumped after anything that moves money or positions, so the Orders / Portfolio tabs
    // re-read instead of waiting to be re-opened.
    const [pairTabsRefreshKey, setPairTabsRefreshKey] = useState(0);
    // The run the Logs tab follows. Seeded from the navigation state, replaced by the run a
    // start creates: pinned to the old one, the tab never showed the new bot's lines.
    const [pairExecutionId, setPairExecutionId] = useState<string | null>(executionId);
    const [pairLogsRefreshKey, setPairLogsRefreshKey] = useState(0);
    // another pair has other runs: drop the pinned one (the first render keeps the navigated id)
    const logsPairRef = useRef(pairDataConfigId);
    useEffect(() => {
        if (logsPairRef.current === pairDataConfigId) return;
        logsPairRef.current = pairDataConfigId;
        setPairExecutionId(null);
        setPairLogsRefreshKey((k) => k + 1);
    }, [pairDataConfigId]);

    // A phase change moves capacity and positions: re-read them once it lands.
    const lastPairPhaseRef = useRef(pairPhase);
    useEffect(() => {
        const prev = lastPairPhaseRef.current;
        lastPairPhaseRef.current = pairPhase;
        if (prev === pairPhase || prev === 'unknown' || pairPhase === 'unknown') return;
        void fetchPairCashState();
        void fetchPositions();
        setPairTabsRefreshKey((k) => k + 1);
        // a start from anywhere (schedule, another tab) opens a new run: follow it
        setPairLogsRefreshKey((k) => k + 1);
    }, [pairPhase, fetchPairCashState, fetchPositions]);
    useEffect(() => {
        if (!pairDataConfigId) {
            setBotOrders([]);
            setBotOrdersStalled(0);
            setBotOrdersError(null);
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                const res = await pairTradingApi.getBotOrders(pairDataConfigId);
                if (cancelled) return;
                setBotOrders(res.orders ?? []);
                setBotOrdersStalled(res.stalledCount ?? 0);
                setBotOrdersError(null);
            } catch (err: any) {
                if (cancelled) return;
                setBotOrdersError(err?.response?.data?.message ?? 'Failed to load bot orders');
            }
        };
        void load();
        const timer = setInterval(() => void load(), pairBotActive ? 3000 : 20000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [pairDataConfigId, pairBotActive]);

    /**
     * Re-hydrate the whole form from the DB-backed /pair/settings endpoint.
     *
     * Needed after any op that moves persisted config out from under the form:
     * a cash op (stock_allocated_cash / deriv_allocated_cash move when the BE
     * shifts currentCapital) or a futures roll (deriv_symbol AND the stamped
     * reference price both change). Patching local state instead would leave
     * `futureLegSymbol`, which reads off existingPairConfig.deriv_symbol, stale.
     */
    const reloadPairSettings = React.useCallback(async (context: string) => {
        if (!selectedPortfolioId) return;
        try {
            const token = localStorage.getItem('access_token');
            const res = await coreService.get(`/pair/settings/${selectedPortfolioId}`, {
                params: pairDataConfigId ? { dataConfigId: pairDataConfigId } : undefined,
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
            if (res?.data) {
                setExistingPairConfig(res.data);
                await loadPairConfigIntoForm(res.data);
                markSettingsSaved(); // reloaded persisted config = clean baseline
            }
        } catch (err) {
            console.error(`Failed to refresh pair settings after ${context}:`, err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPortfolioId, pairDataConfigId, loadPairConfigIntoForm, markSettingsSaved]);

    const handlePairCashSuccess = () => {
        setPairModalMode(null);
        setPairTabsRefreshKey((k) => k + 1);
        void fetchPairCashState();
        void reloadPairSettings('cash op');
    };

    // Option B Start: separate from "Save Config" so the user can create a
    // pair, deposit on both legs, then launch the bot as a deliberate step.
    // Enabled only when (a) pair persists, (b) bot is NOT already running,
    // (c) both legs have been FUNDED (currentCapital > 0). Start launches the
    // bot — it does NOT open a position — so we gate on allocated capital, not
    // idle. This mirrors BE.startPair's guard (currentCapital > 0) exactly; a
    // leg fully deployed in holdings (idle = 0) must still be allowed to start.
    const [startingPair, setStartingPair] = useState(false);
    const startDisabled = useMemo(() => {
        if (!pairDataConfigId || pairReadOnly) return true;
        if (isTerminalAccount(selectedPortfolio)) return true;
        // PAIR-only: a running grid on the same portfolio must NOT block Start.
        if (pairBotActive) return true;
        const stockAllocated = Number(pairCapacity?.stockAllocated ?? 0);
        const derivAllocated = Number(pairCapacity?.derivAllocated ?? 0);
        return !(stockAllocated > 0 && derivAllocated > 0);
    }, [pairDataConfigId, pairCapacity, pairReadOnly, pairBotActive, selectedPortfolio]);

    // Close All needs the opposite of Start: a bot that IS running, because the
    // worker is what places the closing orders (BE refuses otherwise).
    const flattenDisabled = !pairDataConfigId || !pairBotActive || pairReadOnly;

    // Structural edits (add / remove a stock symbol) are only rejected by the
    // backend while the pair is actually RUNNING — a funded-but-not-started pair
    // is fully editable (idle capital just re-splits to the new symbol set, no
    // withdraw needed). Gate the add/remove controls on `pairBotActive` so the
    // user sees a disabled state instead of a mid-session 409. Weight/reference
    // edits stay enabled: the backend allows those live.
    const structuralEditLocked = pairBotActive || pairReadOnly;
    // a field the BE refuses to change while the pair runs is locked here too (same table the
    // terminal reads, GET /change-policy)
    const { refusedWhileRunning } = useChangePolicy();
    const lockedLive = (field: string) => pairBotActive && refusedWhileRunning('PAIR', field);
    /** The add-symbol search popover, and its own query so it cannot clear the row's. */
    const [addStockOpen, setAddStockOpen] = useState(false);
    const [addStockQuery, setAddStockQuery] = useState('');

    const handleStartPair = React.useCallback(async (): Promise<boolean> => {
        if (!pairDataConfigId) return false;
        try {
            setStartingPair(true);
            const res = await pairTradingApi.startPair(pairDataConfigId);
            appMessage.success(res.alreadyRunning ? 'Pair already running.' : res.message ?? 'Pair started.');
            void fetchPairCashState();
            void refreshPairPhase();
            // as the grid does: show the logs of the run just started. No id → the viewer
            // takes the latest run itself.
            setPairExecutionId(res.executionId ?? null);
            setPairLogsRefreshKey((k) => k + 1);
            setBottomTab('logs');
            return true;
        } catch (e: any) {
            const open409 = importInProgressOf(e);
            if (open409) {
                setStartImportConflict(open409);
                return false;
            }
            appMessage.error(e?.response?.data?.message ?? e?.message ?? 'Failed to start pair');
            return false;
        } finally {
            setStartingPair(false);
        }
    }, [pairDataConfigId, appMessage, fetchPairCashState, refreshPairPhase]);

    // The Start modal summarises the FORM, but the bot runs the SAVED config.
    // With unsaved edits those two disagree, so Start must persist first and
    // only launch when the PATCH went through — otherwise the operator confirms
    // one configuration and the engine trades another.
    const handleUpdateAndStartPair = React.useCallback(async (): Promise<boolean> => {
        const saved = await handleSavePairSettings();
        if (!saved) return false;
        return handleStartPair();
    }, [handleSavePairSettings, handleStartPair]);

    // What the operator would be committing to if they confirm "Update & Start".
    const unsavedSettingsChanges = useMemo(
        () => (settingsDirty && pairDataConfigId ? diffPairSettings(savedSignature, settingsSignature) : []),
        [settingsDirty, pairDataConfigId, savedSignature, settingsSignature],
    );

    // Action only — confirmation is rendered by the surrounding <Popconfirm>
    // in the Trading Parameters action row (mirrors grid's StopOutlined flow).
    const handleStopPair = React.useCallback(async () => {
        if (!pairDataConfigId) return;
        try {
            setPairStopLoading(true);
            const res = await pairTradingApi.stopPair(pairDataConfigId);
            // The server leaves the execution STOPPING until the worker reports it finished.
            appMessage.success(res.status === 'NOT_RUNNING' ? 'Pair was not running.' : 'Stopping the pair…');
            void fetchPairCashState();
            void refreshPairPhase();
        } catch (e: any) {
            appMessage.error(e?.response?.data?.message ?? e?.message ?? 'Stop failed');
        } finally {
            setPairStopLoading(false);
        }
    }, [pairDataConfigId, appMessage, fetchPairCashState, refreshPairPhase]);

    // Close every open pair now, keeping the bot running (paused).
    //
    // The BE only queues the command — the close needs real fills — so we poll the
    // worker's report and surface `reason` when anything is left open. Without that
    // the user would see a success toast for a close that closed nothing (market
    // shut, lock busy, a leg that wouldn't fill).
    const handleFlattenPair = React.useCallback(async () => {
        if (!pairDataConfigId) return;
        setPairFlattenLoading(true);
        try {
            const { requestId } = await pairTradingApi.flattenPair(pairDataConfigId);
            appMessage.info('Closing all positions…');

            // ~30s of polling: closing a handful of pairs is seconds of fills, and
            // the report is written even when nothing could be closed.
            for (let i = 0; i < 30; i += 1) {
                await new Promise((r) => setTimeout(r, 1000));
                const res = await pairTradingApi.getFlattenResult(pairDataConfigId, requestId);
                if (res?.status !== 'DONE') continue;
                const { closed = 0, remaining = 0, reason } = res;
                if (remaining === 0) {
                    appMessage.success(
                        closed > 0
                            ? `Closed ${closed} pair(s). The bot is still running with opening paused.`
                            : 'No open pairs to close.',
                    );
                } else {
                    appMessage.warning(
                        `Closed ${closed}, ${remaining} pair(s) still open` +
                            (reason ? ` (${reason})` : '') +
                            '. Try again.',
                    );
                }
                void fetchPairCashState();
                setPairTabsRefreshKey((k) => k + 1);
                return;
            }
            appMessage.warning(
                'No result yet — check the Position tab in a few minutes.',
            );
            void fetchPairCashState();
        } catch (e: any) {
            appMessage.error(e?.response?.data?.message ?? e?.message ?? 'Close all positions failed');
        } finally {
            setPairFlattenLoading(false);
        }
    }, [pairDataConfigId, appMessage, fetchPairCashState]);

    // Re-fetch capacity whenever a place-pair finishes so the cash-op buttons
    // appear right after the first place without requiring a portfolio reselect.
    useEffect(() => {
        if (placePairResult?.success) {
            void fetchPairCashState();
        }
    }, [placePairResult, fetchPairCashState]);

    // Daily schedule fetch/save/delete. Filter to the active pair strategy so a
    // grid schedule on the same portfolio doesn't accidentally render here.
    const fetchSchedule = React.useCallback(async () => {
        if (!selectedPortfolioId) return;
        try {
            setScheduleLoading(true);
            const res = await coreService.get('/daily');
            const raw = res.data?.data ?? res.data;
            const list: PairDailySchedule[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
            const match = list.find(
                (s) => s.portfolioId === selectedPortfolioId && (!strategyId || s.strategyId === strategyId),
            );
            setSchedule(match ?? null);
        } catch {
            setSchedule(null);
        } finally {
            setScheduleLoading(false);
        }
    }, [selectedPortfolioId, strategyId]);

    useEffect(() => {
        fetchSchedule();
    }, [fetchSchedule]);

    // A successful placePair auto-creates a daily schedule on the BE. Refetch
    // so the Place Pair result modal can show (and let the user edit) it right
    // away — placePairResult identity alone doesn't change fetchSchedule.
    useEffect(() => {
        if (placePairResult?.success) void fetchSchedule();
    }, [placePairResult, fetchSchedule]);

    // A place may create a new pair, or a new run: let the Logs tab pick up the latest.
    useEffect(() => {
        if (!placePairResult?.success) return;
        setPairExecutionId(null);
        setPairLogsRefreshKey((k) => k + 1);
    }, [placePairResult]);

    // The BE refuses a start between 11:30 and 13:00 — the bot cannot trade
    // through the break, and clearing the previous run's live orders needs an
    // open market. The cron does NOT retry afterwards, so a time in that window
    // is a schedule that silently never starts. Block it at the source.
    const scheduleTimeInNoonBreak = useMemo(() => {
        if (!scheduleTime) return false;
        return !inStartWindow(scheduleTime.hour() * 60 + scheduleTime.minute());
    }, [scheduleTime]);

    const handleSaveSchedule = React.useCallback(async () => {
        if (!selectedPortfolioId || !scheduleTime) return;
        // Mirror of the BE guard — the button is disabled for this, so reaching
        // here means the value came from somewhere else (a restored schedule).
        const minutes = scheduleTime.hour() * 60 + scheduleTime.minute();
        if (minutes >= 11 * 60 + 30 && minutes < 13 * 60) {
            appMessage.warning('Auto-start cannot be scheduled during the lunch break (11:30–13:00).');
            return;
        }
        // Weekdays only (Mon–Fri = 1-5). Mirrors the BE's auto-created schedule
        // (`0 9 * * 1-5`) — the market is closed on weekends, so an edit must
        // not widen the schedule back to every day.
        const cronDaily = `${scheduleTime.minute()} ${scheduleTime.hour()} * * 1-5`;
        try {
            setSavingSchedule(true);
            if (schedule) {
                await coreService.patch(`/daily/${schedule.id}`, { cronDaily, isActive: scheduleActive, expectedUpdatedAt: schedule.updatedAt });
            } else {
                if (!strategyId) {
                    appMessage.warning('Place a pair first to get a strategy id for the schedule.');
                    return;
                }
                await coreService.post('/daily', {
                    portfolioId: selectedPortfolioId,
                    strategyId,
                    cronDaily,
                    isActive: scheduleActive,
                });
            }
            await fetchSchedule();
            setShowScheduleForm(false);
            appMessage.success(schedule ? 'Schedule updated' : 'Schedule created');
        } catch (err: any) {
            appMessage.error(err?.response?.data?.message || 'Failed to save schedule');
        } finally {
            setSavingSchedule(false);
        }
    }, [selectedPortfolioId, strategyId, scheduleTime, scheduleActive, schedule, fetchSchedule, appMessage]);

    const handleDeleteSchedule = React.useCallback(async () => {
        if (!schedule) return;
        try {
            setSavingSchedule(true);
            await coreService.delete(`/daily/${schedule.id}`);
            setSchedule(null);
            setShowScheduleForm(false);
            appMessage.success('Schedule removed');
        } catch (err: any) {
            appMessage.error(err?.response?.data?.message || 'Failed to delete schedule');
        } finally {
            setSavingSchedule(false);
        }
    }, [schedule, appMessage]);

    const parseCronToTime = (cron: string): string => {
        const parts = cron.split(' ');
        if (parts.length >= 2) {
            return `${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`;
        }
        return cron;
    };

    // Stock table columns
    /**
     * Column widths the two legs SHARE, so the stacked tables line up as one instrument
     * list. Percentages under a fixed layout (LegTableWrapper): the four columns always fit
     * the config panel, so no input sits behind a horizontal scroll. The live price rides
     * under the symbol instead of taking a column of its own.
     */
    // No leg holds anything ⇒ every PnL cell is the one-pair OPEN trigger, so "/ pair"
    // goes in the column header once. A filled leg puts the badge back on the cells.
    const legHeld = (symbol?: string | null) => Boolean(symbol && positionPnl[symbol]?.quantity);
    const legPnlPerPair = !legHeld(futureLegSymbol) && selectedStocks.every((st) => !legHeld(st.symbol));
    const legPnlTitle = legPnlPerPair ? 'PnL / pair (k₫)' : 'PnL (k₫)';

    const LEG_COL = {
        symbol: '30%',
        targetPrice: '26%',
        /** QTY on futures, Proportion on equity. */
        qtyOrProportion: '18%',
        pnl: '26%',
    };

    /** Column order for the equity leg, matching the futures leg's. */
    const STOCK_COLUMN_ORDER = [
        'symbol',
        // The two numbers the operator types read together, ahead of the PnL readout.
        // QTY takes the proportion slot on the futures leg.
        'referencePrice',
        'proportion',
        'positionPnl',
        'flashVolumeLimit',
    ];

    const stockColumns = [
        {
            title: 'Symbol',
            dataIndex: 'symbol',
            key: 'symbol',
            width: LEG_COL.symbol,
            // Remove sits against the symbol it acts on. There is no edit button beside it:
            // proportion and target price are typed in their own cells on this row, which
            // is what the modal behind that button used to be for.
            render: (text: string, record: StockInstrument) => {
                // Live matched price (thousand VND) under the symbol, not `record.price`:
                // that is the snapshot from search/config load and never moves.
                const live = livePriceOf(livePrices, record.symbol);
                return (
                    <LegSymbolCell
                        name={
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <strong>{text}</strong>
                                <Tooltip title={structuralEditLocked ? 'Stop the pair to remove a symbol.' : 'Remove symbol'}>
                                    <EditProportionButton
                                        disabled={structuralEditLocked}
                                        onClick={() => { if (!structuralEditLocked) handleRemoveStock(record.symbol); }}
                                    >
                                        <DeleteOutlined />
                                    </EditProportionButton>
                                </Tooltip>
                            </span>
                        }
                        price={formatDecimal(live ?? record.price ?? 0, 2)}
                        priceMissing={live == null}
                        age={<PriceAge priceData={livePrices[record.symbol]} />}
                    />
                );
            }
        },
        {
            // Filled ⇒ real unrealized PnL of the shares held, marked to the live
            // price. Flat ⇒ what ONE pair's slice of this symbol would make if
            // the pair opened now at the reference prices, badged PER PAIR so it
            // cannot be read as a holding. See utils/pairPnlDisplay.ts.
            title: (
                <Tooltip title="Filled legs show real unrealized PnL. Flat legs show the OPEN trigger's contribution for ONE pair (1 contract), not scaled by allocated cash.">
                    <span>{legPnlTitle}</span>
                </Tooltip>
            ),
            key: 'positionPnl',
            width: LEG_COL.pnl,
            align: 'right' as const,
            render: (_: any, record: StockInstrument) => (
                <PnlCellDisplay
                    cell={resolvePnlCell({
                        position: positionPnl[record.symbol],
                        engineLeg: engineStockLegs[record.symbol],
                        previewLeg: previewBreakdown.stocks[record.symbol],
                        engine: engineReferencePnl,
                    })}
                    quantityUnit="shares"
                    hidePerPairBadge={legPnlPerPair}
                    // `averageCost` is raw VND/share on the position source; the
                    // reference prices are quoted in thousand VND.
                    costDivisor={1000}
                    undefinedReason={referencePnl.undefinedReason}
                />
            )
        },
        {
            title: 'Weight',
            key: 'proportion',
            width: LEG_COL.qtyOrProportion,
            // Sits under QTY on the futures leg, which is right-aligned.
            align: 'right' as const,
            // Editable in place, clamped to the basket's remaining budget by
            // handleInlineProportion — see the guard there.
            render: (_: any, record: StockInstrument) => (
                <InputNumber
                    // Unset is flagged: the bot cannot size the leg without it.
                    className={`leg-price-input pair-edit-input${record.proportion ? '' : ' is-missing'}`}
                    size="small"
                    style={{ width: '100%', fontWeight: 600 }}
                    value={record.proportion ?? null}
                    onChange={(val) => handleInlineProportion(record.symbol, val as number | null)}
                    min={0}
                    max={1}
                    step={0.1}
                    precision={1}
                    // No stepper, like the two price cells beside it.
                    controls={false}
                    placeholder="Required"
                />
            )
        },
        {
            // v3 reference-PnL trigger: per-stock reference price (thousand VND).
            // Required (> 0) to start the bot, so flag missing values as a warning.
            // Called "Target Price" on the futures leg. Same thing — the mark the open
            // trigger measures against — so it reads the same on both.
            title: 'Target Price',
            key: 'referencePrice',
            width: LEG_COL.targetPrice,
            align: 'right' as const,
            // Editable in place, like the futures leg's target price directly above it.
            render: (_: any, record: StockInstrument) => (
                <InputNumber
                    className={`leg-price-input pair-edit-input${record.referencePrice != null ? '' : ' is-missing'}`}
                    size="small"
                    style={{ width: '100%', fontWeight: 600 }}
                    value={record.referencePrice ?? null}
                    onChange={(val) => handleInlineReferencePrice(record.symbol, val as number | null)}
                    min={0}
                    step={0.1}
                    precision={2}
                    // No stepper: the arrows overlay the value in a cell this narrow,
                    // and a price is typed, not nudged.
                    controls={false}
                    placeholder="Required"
                />
            )
        },
        // Flash limit is not released yet — see SHOW_FLASH_VOLUME_LIMIT. Filtered out below
        // rather than removed, so the column comes back with the field it belongs to.
        {
            title: 'Flash Limit',
            key: 'flashVolumeLimit',
            width: '18%',
            align: 'right' as const,
            // Editable like the other two config cells — the modal that used to own this
            // field is gone, so a read-only cell would leave it with no editor at all.
            render: (_: any, record: StockInstrument) => (
                <InputNumber
                    className="leg-price-input pair-edit-input"
                    size="small"
                    style={{ width: '100%', fontWeight: 600 }}
                    value={record.flashVolumeLimit ?? null}
                    onChange={(val) => handleInlineFlashLimit(record.symbol, val as number | null)}
                    min={0}
                    step={100}
                    controls={false}
                    placeholder="default"
                />
            )
        },
    ]
        .filter((c) => SHOW_FLASH_VOLUME_LIMIT || c.key !== 'flashVolumeLimit')
        // Reference prices exist only for the REFERENCE trigger.
        .filter((c) => usesReference || c.key !== 'referencePrice')
        // Ordered like the futures leg above — target, then market, then PnL — so the two
        // legs read as one instrument list rather than two tables that happen to be stacked.
        // Proportion is equity-only, so it goes after the columns they share.
        .sort((a, b) => STOCK_COLUMN_ORDER.indexOf(a.key) - STOCK_COLUMN_ORDER.indexOf(b.key));

    // Prevent page scrolling - everything should fit on one page
    useEffect(() => {
        // Prevent body scrolling
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            // Restore scrolling when component unmounts
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, []);

    // Daily auto-start schedule editor — reused both inline in the params card
    // and inside the Start confirmation modal (so it can be configured at the
    // same point you launch, like grid).
    const scheduleSection = (
        <div style={{ marginTop: '8px' }}>
            {schedule && !showScheduleForm ? (
                <div style={{
                    background: '#1E2329',
                    border: '1px solid #2B3139',
                    borderRadius: 4,
                    padding: '6px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <CheckCircleOutlined style={{ color: schedule.isActive ? '#0ECB81' : '#848E9C', fontSize: 14 }} />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#E0E0E0' }}>
                                Daily {parseCronToTime(schedule.cronDaily)}
                            </div>
                            <div style={{ fontSize: 10, color: schedule.isActive ? '#0ECB81' : '#848E9C' }}>
                                {schedule.isActive ? 'Active' : 'Paused'}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            style={{ color: '#0ECB81', height: 24, width: 24, padding: 0 }}
                            onClick={() => {
                                setScheduleTime(dayjs(parseCronToTime(schedule.cronDaily), 'HH:mm'));
                                setScheduleActive(schedule.isActive);
                                setShowScheduleForm(true);
                            }}
                        />
                        <Popconfirm
                            title="Remove schedule"
                            description="The pair will no longer auto-start daily."
                            onConfirm={handleDeleteSchedule}
                            okText="Remove"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true, size: 'middle', loading: savingSchedule }}
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                style={{ color: '#F6465D', height: 24, width: 24, padding: 0 }}
                            />
                        </Popconfirm>
                    </div>
                </div>
            ) : showScheduleForm ? (
                <div style={{
                    background: '#1E2329',
                    border: '1px solid #2B3139',
                    borderRadius: 4,
                    padding: '8px 10px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <ClockCircleOutlined style={{ color: '#848E9C', fontSize: 12 }} />
                        <span style={{ fontSize: 11, color: '#848E9C', fontWeight: 600 }}>
                            Daily auto-start
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <TimePicker
                            value={scheduleTime}
                            onChange={setScheduleTime}
                            format="HH:mm"
                            size="small"
                            minuteStep={5}
                            style={{ flex: 1 }}
                            status={scheduleTimeInNoonBreak ? 'error' : undefined}
                            disabledTime={startWindowDisabledTime}
                        />
                        <Switch
                            checked={scheduleActive}
                            onChange={setScheduleActive}
                            size="small"
                            checkedChildren="On"
                            unCheckedChildren="Off"
                        />
                    </div>
                    {scheduleTimeInNoonBreak && (
                        <div style={{ fontSize: 10, color: '#F6465D', marginBottom: 8, lineHeight: 1.4 }}>
                            A bot can only be scheduled to start between {START_WINDOWS_TEXT}: outside
                            them the broker is not trading, and a start is skipped, not retried.
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                        <Button
                            size="small"
                            onClick={() => setShowScheduleForm(false)}
                            style={{ flex: 1, height: 28, fontSize: 11 }}
                        >
                            Cancel
                        </Button>
                        <Tooltip title={scheduleTimeInNoonBreak ? `Pick a start between ${START_WINDOWS_TEXT}.` : undefined}>
                            <Button
                                size="small"
                                type="primary"
                                loading={savingSchedule}
                                disabled={scheduleTimeInNoonBreak}
                                onClick={handleSaveSchedule}
                                style={{
                                    flex: 1,
                                    height: 28,
                                    fontSize: 11,
                                    background: scheduleTimeInNoonBreak ? undefined : '#0ECB81',
                                    borderColor: scheduleTimeInNoonBreak ? undefined : '#0ECB81',
                                }}
                            >
                                {schedule ? 'Update' : 'Save'}
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            ) : (
                <Tooltip title={!strategyId ? 'Place a pair first to enable scheduling.' : 'Configure a daily auto-start time for this pair.'}>
                    <Button
                        size="small"
                        icon={<ClockCircleOutlined />}
                        loading={scheduleLoading}
                        disabled={!strategyId}
                        onClick={() => {
                            setScheduleTime(dayjs('09:00', 'HH:mm'));
                            setScheduleActive(true);
                            setShowScheduleForm(true);
                        }}
                        style={{
                            width: '100%',
                            height: '32px',
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '4px',
                            // Matches the summary tiles; antd's default 0 2px 0 shadow
                            // read as a stray dark line under the row.
                            background: '#1E2329',
                            borderColor: '#2B3139',
                            color: '#848E9C',
                            boxShadow: 'none',
                        }}
                    >
                        Configure Daily Schedule
                    </Button>
                </Tooltip>
            )}
        </div>
    );

    // What the operator just configured — shared by the Start confirmation modal
    // and the post-save Success modal so both read the same live state.
    // ---- Bot Orders panel -------------------------------------------------
    // Lives at the bottom of the config column. Sourced from the bot's Redis
    // order journal, so it shows intent (CREATED / SENT_TO_BROKER) as well as
    // outcome — the broker and accounting APIs only ever show the outcome.
    const botOrderStatusStyle = (order: PairBotOrder): { color: string; label: string } => {
        if (order.status === 'FILLED') return { color: '#0ECB81', label: 'FILLED' };
        if (order.status === 'REJECTED') return { color: '#F6465D', label: 'REJECTED' };
        if (order.status === 'CANCELLED' || order.status === 'EXPIRED') {
            return { color: '#848E9C', label: order.status };
        }
        // Non-final. ageSeconds < 0 means the bot placed it and never recorded a
        // single execution report — a lost order-update route looks exactly like
        // this, so it reads as the loudest state, not the quietest.
        if (order.ageSeconds < 0) return { color: '#F0B90B', label: 'NO ACK' };
        if (order.ageSeconds >= 30) return { color: '#F0B90B', label: `${order.status} ${order.ageSeconds}s` };
        return { color: '#E0E0E0', label: order.status };
    };

    const botOrdersSection = (
        <div style={{ marginTop: '10px' }}>
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: '4px',
            }}>
                <Label style={{ fontSize: '11px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Bot Orders
                    <Tooltip title="Today's orders as the BOT recorded them, from its own journal — not the broker. Includes orders placed but never acknowledged, which is how a stuck bot looks. Prices are in native units: thousand-VND for stocks, index points for futures.">
                        <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                    </Tooltip>
                </Label>
                {botOrders.length > 0 && (
                    <span style={{ fontSize: '10px', color: botOrdersStalled > 0 ? '#F0B90B' : '#848E9C' }}>
                        {botOrders.filter((o) => !o.isFinal).length} live
                        {botOrdersStalled > 0 ? ` · ${botOrdersStalled} stalled` : ''}
                        {` · ${botOrders.length} today`}
                    </span>
                )}
            </div>

            {botOrdersError ? (
                <div style={{ fontSize: '11px', color: '#F6465D', padding: '6px 0' }}>{botOrdersError}</div>
            ) : !pairDataConfigId ? (
                <div style={{ fontSize: '11px', color: '#848E9C', padding: '6px 0' }}>
                    Save the pair config first.
                </div>
            ) : botOrders.length === 0 ? (
                <div style={{ fontSize: '11px', color: '#848E9C', padding: '6px 0' }}>
                    No orders today.
                </div>
            ) : (
                // Own scroll box: the config column already scrolls, and letting
                // this list grow would push Start/Stop out of reach.
                <div style={{
                    maxHeight: '188px', overflowY: 'auto', overflowX: 'hidden',
                    border: '1px solid #2B3139', borderRadius: '4px', background: '#1A1D23',
                }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', tableLayout: 'fixed' }}>
                        <thead>
                            <tr style={{ color: '#848E9C' }}>
                                <th style={{ textAlign: 'left', padding: '3px 4px', fontWeight: 600, width: '18%' }}>Time</th>
                                <th style={{ textAlign: 'left', padding: '3px 4px', fontWeight: 600, width: '24%' }}>Symbol</th>
                                <th style={{ textAlign: 'left', padding: '3px 4px', fontWeight: 600, width: '11%' }}>Side</th>
                                <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 600, width: '17%' }}>Filled</th>
                                <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 600, width: '14%' }}>Price</th>
                                <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 600, width: '16%' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {botOrders.map((order) => {
                                const status = botOrderStatusStyle(order);
                                const isBuy = order.side.includes('BUY');
                                // Show what actually happened when it did, else what
                                // the bot asked for — avg only exists after a fill.
                                const price = order.avgPrice ?? order.limitPrice;
                                return (
                                    <tr key={order.clientOrderId} style={{ borderTop: '1px solid #23272E' }}>
                                        <td style={{ padding: '3px 4px', color: '#848E9C', fontFamily: "'Courier New', monospace" }}>
                                            {order.lastModified
                                                ? new Date(order.lastModified * 1000).toLocaleTimeString('vi-VN', { hour12: false })
                                                : '—'}
                                        </td>
                                        <td style={{
                                            padding: '3px 4px', color: '#E0E0E0', fontWeight: 600,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            // Left edge marks the leg, so no column is spent on it.
                                            borderLeft: `2px solid ${order.leg === 'FUTURES' ? '#F0B90B' : '#2B7CD3'}`,
                                        }}>
                                            {order.symbol}
                                        </td>
                                        <td style={{ padding: '3px 4px', color: isBuy ? '#0ECB81' : '#F6465D', fontWeight: 700 }}>
                                            {isBuy ? 'BUY' : 'SELL'}
                                        </td>
                                        <td style={{
                                            padding: '3px 4px', textAlign: 'right', fontFamily: "'Courier New', monospace",
                                            color: order.matchedQuantity >= order.quantity ? '#0ECB81'
                                                : order.matchedQuantity > 0 ? '#F0B90B' : '#848E9C',
                                        }}>
                                            {order.matchedQuantity}/{order.quantity}
                                        </td>
                                        <td style={{ padding: '3px 4px', textAlign: 'right', color: '#E0E0E0', fontFamily: "'Courier New', monospace" }}>
                                            {price === null ? '—' : formatCompact(price, 2)}
                                        </td>
                                        <td style={{ padding: '3px 4px', textAlign: 'right', color: status.color, fontWeight: 600 }}>
                                            {status.label}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    const configSummary = (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
                {[
                    ['Future', selectedFuture?.symbol ?? '—'],
                    ['Future Ref Price', futureInstrumentPrice ?? '—'],
                    ['Future Leg', futureLegIsShort ? 'SELL (short)' : 'BUY (long)'],
                    ['Mode', usesReference ? 'Waiting' : 'Auto'],
                    ['Close Type', 'RETURN (TP/CL)'],
                    ['TP / SL (%)', `${takeProfitReturn} / ${cutLossReturn}`],
                    ['Max Open', maxOpen === 0 ? '∞' : maxOpen],
                    ['Max Holding Days', maxHoldingDays === 0 ? 'Off' : maxHoldingDays],
                    ['Pause Opening', isPause ? 'Paused' : 'Active'],
                    ['Stock / Deriv Cash', `${formatNumber(stockAllocatedCash ?? 0)} / ${formatNumber(derivAllocatedCash ?? 0)}`],
                ].map(([label, value]) => (
                    <div key={String(label)} style={{ background: '#1E2329', border: '1px solid #2B3139', borderRadius: 4, padding: '6px 10px' }}>
                        <div style={{ color: '#848E9C', fontSize: 11, marginBottom: 2 }}>{label}</div>
                        <div style={{ color: '#EAECEF', fontSize: 13, fontWeight: 600 }}>{String(value)}</div>
                    </div>
                ))}
            </div>

            {/* Stock legs: symbol · ratio · reference price */}
            <div style={{ marginBottom: 12 }}>
                <div style={{ color: '#848E9C', fontSize: 11, marginBottom: 4 }}>
                    Stock legs ({selectedStocks.length}) — symbol · ratio · ref price
                </div>
                {selectedStocks.length === 0 ? (
                    <div style={{ color: '#848E9C', fontSize: 12 }}>No stocks selected.</div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {selectedStocks.map((s) => (
                            <span key={s.symbol} style={{ background: '#1E2329', border: '1px solid #2B3139', borderRadius: 4, padding: '4px 8px', fontSize: 12, color: '#EAECEF' }}>
                                {s.symbol} · {s.proportion ?? 0} · {s.referencePrice ?? '—'}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </>
    );

  const updateButton = (
    <Tooltip
        title={
            pairDataConfigId && !settingsDirty
                ? 'No changes to update — go straight to Start.'
                : undefined
        }
    >
        <Button
            data-tour="pair-save"
            type="primary"
            size="small"
            icon={pairDataConfigId ? <EditOutlined /> : <SaveOutlined />}
            loading={savingSettings || placingPair}
            onClick={pairDataConfigId ? handleSavePairSettings : handlePlacePairStrategy}
            // Once a pair is persisted, only enable Update when the
            // settings actually changed (settingsDirty). First-time
            // placement (no dataConfigId) always needs Save.
            disabled={
                // No pair resolved yet, or an ended pair: nothing to save into.
                !selectedPairKey || pairReadOnly ||
                // An empty basket blocks only the FIRST placement —
                // there'd be nothing to place. Updating an existing
                // pair down to zero symbols is allowed: the BE parks
                // the stock capital and reseeds it when symbols return.
                (!pairDataConfigId && selectedStocks.length === 0) ||
                savingSettings ||
                placingPair ||
                !selectedFuture ||
                (!!pairDataConfigId && !settingsDirty)
            }
            style={{
                width: '100%',
                fontWeight: 600,
                fontSize: '12px',
                height: '32px',
                borderRadius: '4px',
                marginTop: '14px',
                marginBottom: '8px',
            }}
        >
            {pairDataConfigId ? 'Update' : 'Save'}
        </Button>
    </Tooltip>
  );

  /**
   * Compact shell renders one config group at a time; the rest stay mounted but hidden
   * so nothing in this panel remounts (and loses local state) as the pills change.
   */
  const pairSection = (name: string): React.CSSProperties | undefined =>
      isMobile && pairMobileSection !== name ? { display: 'none' } : undefined;

  const pairChartNode = (
    <div data-tour="pair-chart" style={{ width: '100%', height: '100%', minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        {chartView === 'price' ? (
            <CandlestickChart
                symbol={chartContract.symbol ?? ''}
                height={0}
                interval={chartInterval}
                priceData={futureHeaderData}
                headerExtra={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Select
                            size="small"
                            value={chartInterval}
                            onChange={(v) => setChartInterval(v as typeof chartInterval)}
                            options={[
                                { value: '5s', label: '5 sec' },
                                { value: '1min', label: '1 min' },
                                { value: '5min', label: '5 min' },
                                { value: '1h', label: '1 hour' },
                                { value: '1day', label: '1 day' },
                                { value: '1w', label: '1 week' },
                            ]}
                            style={{ width: 90 }}
                        />
                        <ChartViewToggle value={chartView} onChange={setChartView} />
                    </div>
                }
            />
        ) : (
            <PairPnlChart
                samples={pnlHistory}
                notionalHistory={notionalHistory}
                live={{
                    futValue: legNotional.futValue,
                    equityValue: legNotional.equityValue,
                }}
                interval={pnlInterval}
                height={0}
                headerExtra={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Select
                            size="small"
                            value={pnlInterval}
                            onChange={(v) => setPnlInterval(v as PnlInterval)}
                            style={{ width: 90 }}
                            options={[
                                { value: '5s', label: '5 sec' },
                                { value: '1min', label: '1 min' },
                                { value: '5min', label: '5 min' },
                                { value: '1h', label: '1 hour' },
                                // Daily and up stay off: the spread resets to 0 at
                                // every previous close, so a daily bar collapses each
                                // session to a single point.
                                { value: '1day', label: '1 day', disabled: true },
                                { value: '1w', label: '1 week', disabled: true },
                            ]}
                        />
                        <ChartViewToggle value={chartView} onChange={setChartView} />
                    </div>
                }
            />
        )}
    </div>
  );

  // Derivatives account + pair-level cash ops, at the top of Trading Parameters like the
  // grid's capital panel. Not under the read-only ConfigProvider: the buttons gate themselves.
  const pairCapitalControls = (
    <>
      {/* ----- Dual-leg allocation: first config of a pair. Capital lives in
          DB (InstrumentConfig.currentCapital) and mutates via Deposit/Withdraw
          below, so this whole block also serves as the live capital view. ----- */}
      {/* Balance Mode hidden: always NOTIONAL_MATCH. The state stays
          pinned to its NOTIONAL_MATCH default — see balanceMode useState.
          Single column: this Grid has one child, and at $columns={2} the
          select only got half the row, which truncated portfolio names to
          "Future Portf…". */}
      <Grid $columns={1} style={{ gap: '8px' }}>
          <FormGroup style={{ marginBottom: '6px' }}>
              <Label style={{ fontSize: '11px', marginBottom: '0' }}>Derivatives Portfolio</Label>
              <Select
                  style={{ width: '100%' }}
                  size="small"
                  placeholder="Select a derivatives portfolio"
                  value={derivPortfolioId}
                  onChange={(v) => setDerivPortfolioId(v)}
                  disabled={pairReadOnly}
                  allowClear
                  options={derivPortfolios.map(p => ({
                      value: p.id,
                      label: p.portfolioName,
                  }))}
                  notFoundContent={
                      derivPortfolios.length === 0
                          ? 'No derivatives portfolio'
                          : undefined
                  }
              />
          </FormGroup>
      </Grid>

      {/* Option B flow: cash inputs removed — both legs are funded
          exclusively via these two buttons. Save Config (config
          column) persists the pair with 0 capital; Deposit then
          fills each leg, and Start launches the bot once both legs
          have non-zero idle. Always visible so the operator knows
          the actions exist; disabled until a pair is placed and
          refused while the bot is active. */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
          <Tooltip title={pairReadOnly ? 'This pair has ended — read-only.' : !pairDataConfigId ? 'Save the pair config first (the Save Config button below) — then you can deposit.' : pairBotActive ? 'Stop the pair before depositing.' : 'Pair-level deposit (allocator splits across legs).'}>
              <Button
                  size="small"
                  type="primary"
                  icon={<ArrowDownOutlined />}
                  disabled={!pairDataConfigId || pairBotActive || pairReadOnly}
                  onClick={() => setPairModalMode('DEPOSIT')}
                  style={{
                      flex: 1,
                      height: '32px',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '4px',
                      background: (!pairDataConfigId || pairBotActive || pairReadOnly) ? undefined : '#0ECB81',
                      borderColor: (!pairDataConfigId || pairBotActive || pairReadOnly) ? undefined : '#0ECB81',
                  }}
              >
                  Deposit
              </Button>
          </Tooltip>
          <Tooltip title={pairReadOnly ? 'This pair has ended — read-only.' : !pairDataConfigId ? 'Save the pair config first (the Save Config button below) — then you can withdraw.' : pairBotActive ? 'Stop the pair before withdrawing.' : 'Pair-level withdraw from idle cash on both legs.'}>
              <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={!pairDataConfigId || pairBotActive || pairReadOnly}
                  onClick={() => setPairModalMode('WITHDRAW')}
                  style={{
                      flex: 1,
                      height: '32px',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '4px',
                      background: (!pairDataConfigId || pairBotActive || pairReadOnly) ? undefined : '#F6465D',
                      borderColor: (!pairDataConfigId || pairBotActive || pairReadOnly) ? undefined : '#F6465D',
                      color: (!pairDataConfigId || pairBotActive || pairReadOnly) ? undefined : '#fff',
                  }}
              >
                  Withdraw
              </Button>
          </Tooltip>
      </div>
    </>
  );

  const pairBottomTabItems = [
  {
    label: 'Logs',
    key: 'logs',
    children: (
      <div style={{ height: '100%', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto', paddingBottom: '8px' }}>
        <LogViewer
            selectedPortfolio={selectedPortfolio}
            strategyId={strategyId}
            strategyType="PAIR_TRADING"
            executionId={pairExecutionId}
            compact
            showExecutionControls={false}
            refreshKey={pairLogsRefreshKey}
        />
      </div>
    ),
  },
  {
    label: 'Position',
    key: 'position',
    children: (
      <div style={{ height: '100%', overflow: 'auto', padding: '8px' }}>
        {pendingImport && (
          <div style={{ marginBottom: 8 }}>
            <ImportJobProgress
              compact
              importId={pendingImport.id}
              onReady={() => {
                setPendingImport(null);
                void fetchPositions();
                void refreshPairList();
              }}
            />
            {pairPositions.length > 0 && (
              <div style={{ fontSize: 12, color: '#848E9C', marginTop: 4 }}>
                The positions below are the bot's book from before the import.
              </div>
            )}
          </div>
        )}
        {loadingPositions && pairPositions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px', color: '#848E9C' }}>Loading...</div>
        ) : pairPositions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px', color: '#848E9C', fontSize: '12px' }}>
            No positions
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {pairPositions.map((pair) => {
              const pairLabel = pair.legs
                .map((l) => l.instrument.split(':')[1] ?? l.instrument)
                .join(' ⇄ ');
              const pairPnl = pair.totalPnl;
              const pnlColor = pairPnl != null && pairPnl > 0 ? '#0ECB81' : pairPnl != null && pairPnl < 0 ? '#F6465D' : '#848E9C';
              // What the expiry roll banked on the futures leg it closed — cash, part of the PnL.
              const cash = pair.realizedPnl ?? 0;
              const signed = (v: number | null, abbrev = false) =>
                  v == null ? '—' : `${v > 0 ? '+' : ''}${abbrev ? formatAbbrev(v) : formatInt(v)}`;
              const pnlTone = (v: number | null) => (v != null && v > 0 ? '#0ECB81' : v != null && v < 0 ? '#F6465D' : '#848E9C');
              const isCollapsed = collapsedPairs.has(pair.pairId);
              return (
                <div key={pair.pairId} style={{ border: '1px solid #2B3139', borderRadius: 6, overflow: 'hidden' }}>
                  <div
                    onClick={() => togglePairCollapsed(pair.pairId)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#1A1D23', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#E0E0E0', fontWeight: 700, fontSize: '12px' }}>
                      <span style={{ display: 'inline-block', width: '10px', color: '#848E9C', transition: 'transform 0.15s', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>▾</span>
                      Pair · {pairLabel}
                      {refreshingPositions && (
                          <Tooltip title="Refreshing positions">
                              <Spin size="small" style={{ lineHeight: 1 }} />
                          </Tooltip>
                      )}
                      {positionsStale && !refreshingPositions && (
                          <Tooltip title="Last refresh failed — these rows are the previous good read, not live">
                              <span style={{ color: '#F0B90B', fontSize: '11px', fontWeight: 700 }}>stale</span>
                          </Tooltip>
                      )}
                    </span>
                    {/* One line, and never wrapped: it used to break between "PnL" and the
                        figure, which put the total's right edge somewhere other than the
                        legs' below it — the column where lining up is the whole point.
                        Abbreviated on mobile for the same reason. */}
                    <span
                      title={pairPnl == null ? 'No price for a leg yet' : signed(pairPnl)}
                      style={{
                        fontFamily: "'Courier New', monospace",
                        fontWeight: 700,
                        fontSize: '12px',
                        color: pnlColor,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      PnL {signed(pairPnl, isMobile)}
                    </span>
                  </div>
                  {!isCollapsed && (isMobile ? (
                  /* Six columns — instrument, side, qty, cost, current, PnL — do not fit
                     390px, and the browser clipped the last one rather than shrinking:
                     "HNXDS:VN30F2610" and "145,300" both have a min-content width the
                     table cannot go under. Two lines per leg fits, and puts the PnL where
                     the eye lands first instead of off the edge. */
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    // Matches the card header's 6px 8px, so each row's right-aligned PnL
                    // lands in the same column as the pair's total above it.
                    padding: '6px 8px 8px',
                  }}>
                    {pair.legs.map((l) => (
                      <div key={l.instrument} style={{ borderBottom: '1px solid #1A1D23', paddingBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                          <span style={{ color: '#E0E0E0', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.instrument}
                          </span>
                          <span style={{ fontWeight: 700, fontSize: '10px', padding: '1px 5px', borderRadius: 3, flexShrink: 0, color: l.side === 'SHORT' ? '#F6465D' : '#0ECB81', background: l.side === 'SHORT' ? 'rgba(246,70,93,0.12)' : 'rgba(14,203,129,0.12)' }}>
                            {l.side}
                          </span>
                          <span
                            title={signed(l.pnl)}
                            style={{ marginLeft: 'auto', flexShrink: 0, fontWeight: 700, fontFamily: "'Courier New', monospace", color: pnlTone(l.pnl) }}
                          >
                            {signed(l.pnl, true)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 10, fontSize: 10.5, color: '#848E9C', marginTop: 2 }}>
                          <span>Qty <b style={{ color: '#E0E0E0' }}>{formatNumber(l.quantity)}</b></span>
                          <span>
                            {formatNumber(l.costPrice)}
                            <span style={{ color: '#5E6673' }}> {'\u2192'} </span>
                            <b style={{ color: '#E0E0E0' }}>{l.currentPrice == null ? '—' : formatNumber(l.currentPrice)}</b>
                          </span>
                        </div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                      <Tooltip title="Realized on the futures leg the expiry roll closed. Counted in the pair's PnL, as the bot's TP/SL counts it.">
                        <span style={{ color: '#E0E0E0', fontWeight: 600 }}>Cash · realized on roll</span>
                      </Tooltip>
                      <span style={{ marginLeft: 'auto', fontWeight: 700, fontFamily: "'Courier New', monospace", color: pnlTone(cash) }}>{signed(cash, true)}</span>
                    </div>
                  </div>
                  ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ color: '#848E9C', borderBottom: '1px solid #2B3139' }}>
                        <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Instrument</th>
                        <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Side</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Qty</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Cost Price</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Cur. Price</th>
                        <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>PnL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pair.legs.map((l) => (
                        <tr key={l.instrument} style={{ borderBottom: '1px solid #1A1D23' }}>
                          <td style={{ padding: '4px 6px', color: '#E0E0E0', fontWeight: 600 }}>{l.instrument}</td>
                          <td style={{ padding: '4px 6px' }}>
                            <span style={{ fontWeight: 700, fontSize: '10px', padding: '1px 5px', borderRadius: 3, color: l.side === 'SHORT' ? '#F6465D' : '#0ECB81', background: l.side === 'SHORT' ? 'rgba(246,70,93,0.12)' : 'rgba(14,203,129,0.12)' }}>
                              {l.side}
                            </span>
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', color: '#E0E0E0', fontWeight: 600 }}>
                            {formatNumber(l.quantity)}
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', color: '#848E9C' }}>{formatNumber(l.costPrice)}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', color: '#E0E0E0' }}>{l.currentPrice == null ? '—' : formatNumber(l.currentPrice)}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, fontFamily: "'Courier New', monospace", color: pnlTone(l.pnl) }}>
                            {signed(l.pnl)}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderBottom: '1px solid #1A1D23' }}>
                        <td style={{ padding: '4px 6px', color: '#E0E0E0', fontWeight: 600 }} colSpan={2}>
                          <Tooltip title="Realized on the futures leg the expiry roll closed. Counted in the pair's PnL, as the bot's TP/SL counts it.">
                            <span>Cash · realized on roll</span>
                          </Tooltip>
                        </td>
                        <td colSpan={3} />
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, fontFamily: "'Courier New', monospace", color: pnlTone(cash) }}>
                          {signed(cash)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    ),
  },
  {
    label: 'Order Book',
    key: 'orderbook',
    children: (
      <div style={{ width: '100%', height: '100%', minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <BidAsk
            height={0}
            priceData={futurePriceData}
            defaultSplitView
        />
      </div>
    ),
  },
  {
    // Same role as the grid's Import Holdings tab: fix what the bot holds from the broker.
    label: 'Import Holdings',
    key: 'balance',
    children: (
      <div style={{ width: '100%', height: '100%', minHeight: 0, overflow: 'auto', padding: '8px 12px' }}>
        <div style={{ maxWidth: 420 }}>
      <div style={{ color: '#848E9C', fontSize: 12, marginBottom: 2 }}>
          Set what the bot holds from the broker, or rebuild the pair from positions no other
          bot claims. Stop the bot first.
      </div>
      {/* Fix what the bot holds, the pair's counterpart of the grid's Import holding: rebuild the
          pair from the broker (a new config). Needs the bot stopped, and never claims shares
          another bot on the account holds. */}
      <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
          {/* Open while ENDING: a leg still counting shares the broker no longer has keeps the
              pair from settling, and rebuilding it from the broker ends the old one. */}
          <Tooltip title={pairImportLocked ? 'This pair has ended — read-only.' : !pairDataConfigId ? 'Save the pair config first.' : pairBotActive ? 'Stop the pair first.' : selectedPair?.lifecycleStatus === 'ENDING' ? 'Settling is waiting on a leg the broker no longer holds: rebuild the pair from the broker to end it.' : isMobile ? undefined : 'Rebuild the pair from what the broker holds and no other bot claims (a new config; the old one is archived), or end it without selling.'}>
              <Button size="small" icon={<ImportOutlined />} style={{ flex: 1, height: '32px', fontSize: '12px', fontWeight: 600, borderRadius: '4px' }}
                  disabled={!pairDataConfigId || pairBotActive || pairImportLocked}
                  onClick={() => setPairImportOpen(true)}>
                  Import pair from broker
              </Button>
          </Tooltip>
      </div>
        </div>
      </div>
    ),
  },
  ];
  const pairBottomTab = (key: string) =>
      pairBottomTabItems.find((t) => t.key === key)?.children;

  /**
   * Stop bot / Start, plus Update on mobile.
   *
   * Hoisted out of the card because the compact shell pins it BELOW the scrolling column:
   * `position: sticky; bottom: 0` cannot pin a scroller's last child, so keeping these
   * reachable without scrolling the whole config means rendering them outside the scroller.
   */
  /** Pills served by the one config card, and therefore by the pinned action bar. */
  const PAIR_CONFIG_SECTIONS = ['legs', 'trigger', 'risk', 'orders'];

  // Equal flex:1, 32px, mirrors grid's ConfigActionBar. Update rides along only on mobile —
  // on desktop it stays with the pause/close row, because it commits settings while these
  // two drive the bot's lifecycle.
  const pairActionBar = (
  <div data-tour="pair-run" style={{
      flexShrink: 0,
      padding: '8px 0 4px',
      display: 'flex',
      gap: '6px',
  }} className={isMobile ? 'pair-mobile-actions' : undefined}>
      {/* Compact shell: Update joins the pinned bar — mid-form it sat below a
          screen-and-a-half of inputs. */}
      {isMobile && updateButton}
      <Popconfirm
          title="Stop pair bot"
          description="Today's StrategyExecution will be flipped to STOP. Required before pair-level deposit/withdraw or close."
          onConfirm={handleStopPair}
          okText="Stop"
          cancelText="Cancel"
          okButtonProps={{ danger: true, size: 'middle', loading: pairStopLoading, style: { minWidth: 72, height: 32 } }}
          cancelButtonProps={{ size: 'middle', style: { minWidth: 72, height: 32, background: '#2B3139', borderColor: '#3d4148', color: '#E0E0E0' } }}
          disabled={!pairDataConfigId || pairReadOnly || pairStopLoading || !pairBotActive || pairPhase === 'stopping'}
      >
          <Button
              size="small"
              danger
              icon={<StopOutlined />}
              loading={pairStopLoading || pairPhase === 'stopping'}
              disabled={!pairDataConfigId || pairReadOnly || !pairBotActive || pairPhase === 'stopping'}
              style={{
                  flex: 1,
                  fontWeight: 600,
                  fontSize: '12px',
                  height: '32px',
                  borderRadius: '4px',
                  background: 'transparent',
                  borderColor: '#F6465D',
                  color: '#F6465D',
              }}
          >
              {pairPhase === 'stopping' ? 'Stopping…' : 'Stop bot'}
          </Button>
      </Popconfirm>
      <Tooltip
          title={
              pairReadOnly
                  ? 'This pair has ended — read-only.'
                  : !pairDataConfigId
                  ? 'Save the pair config first.'
                  : pairPhase === 'stopping'
                      ? 'Wait for the pair to finish stopping.'
                      : pairPhase === 'starting'
                          ? 'The pair is starting.'
                          : pairBotActive
                              ? 'Pair is already running.'
                              : isTerminalAccount(selectedPortfolio)
                                  ? <>{TERMINAL_START_MESSAGE} <DesktopAppLink onOpen={() => navigate(DESKTOP_APP_PATH)} /></>
                      : startDisabled
                          ? 'Deposit on BOTH legs before starting.'
                          : settingsDirty
                              ? 'Unsaved settings changes — Start will ask you to confirm and update the configuration before launching.'
                              : 'Publish the start payload to the pair bot.'
          }
      >
          <Button
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={() => { void fetchSchedule(); setStartConfirmVisible(true); }}
              loading={startingPair}
              disabled={startDisabled || startingPair}
              style={{
                  flex: 1,
                  background: startDisabled ? undefined : '#F0B90B',
                  borderColor: startDisabled ? undefined : '#F0B90B',
                  color: startDisabled ? undefined : '#1E2329',
                  fontWeight: 600,
                  fontSize: '12px',
                  height: '32px',
                  borderRadius: '4px',
              }}
          >
              Start
          </Button>
      </Tooltip>
  </div>
  );

  const pairConfigCard = (
    <Card
        title={isMobile ? undefined : 'Trading Parameters'}
        /* Config loading is reported here, in the panel whose fields are
           actually being filled, rather than as a full-screen blocking
           overlay: it can run for a while (the loader refetches every
           configured stock over the network), and blanking the whole page
           for that hides the chart and the tabs for no reason. */
        /* On mobile BOTH have to be undefined: antd renders the card head — an empty
           ~56px bar with a bottom border — if either one is set, and the loading span is
           always present even when it has nothing to show. The notice moves into the body
           below instead of being dropped. */
        extra={
            isMobile ? undefined : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {(loadingPairConfig || loadingConfig) && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#848E9C', fontSize: 11 }}>
                            <Spin size="small" />
                            Loading configuration…
                        </span>
                    )}
                </span>
            )
        }
        style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            // Compact shell: the card grows to its content and the page scrolls it, instead
            // of clamping the form to whatever the chart left over.
            ...(isMobile
                ? { height: 'auto', overflow: 'visible', background: 'transparent', border: 'none' }
                : { height: '100%', overflow: 'hidden' }),
        }}
        styles={{
            body: {
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                ...(isMobile
                    ? { flex: 'none', overflow: 'visible', padding: '8px 0 0' }
                    : { flex: 1, overflow: 'hidden' }),
            },
        }}
    >
        {/* paddingLeft leaves room for the focus ring: this column clips
            horizontal overflow, and full-width fields sit flush at x=0, so
            the 1px left border + 2px green glow of a focused input were
            being cut off (only the left edge — the right had the 8px). */}
        <PairConfigScroll style={{
            overflowX: 'hidden',
            paddingLeft: '3px',
            paddingRight: isMobile ? '3px' : '8px',
            minHeight: 0,
            // One scroller per screen — on mobile this is part of the page column.
            ...(isMobile ? { flex: 'none', overflowY: 'visible' } : { flex: 1, overflowY: 'auto' }),
        }}>
            {isMobile && (loadingPairConfig || loadingConfig) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#848E9C', fontSize: 11, marginBottom: 6 }}>
                    <Spin size="small" />
                    Loading configuration…
                </div>
            )}

            <div style={pairSection('legs')}>
                <PairLifecyclePanel
                    item={selectedPair}
                    isNew={isNewPair}
                    newPairName={newPairName}
                    onNewPairNameChange={setNewPairName}
                    onChanged={() => { void refreshPairList(); }}
                    onEnded={handlePairEnded}
                    onCopyToNew={handleCopyToNewPair}
                />
            </div>

            {/* Mode right under the pair name: it decides which inputs the rest of the form asks for. */}
            <div data-tour="pair-entry" style={{ ...pairSection('legs'), marginTop: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#EAECEF' }}>Mode</span>
                    <Tooltip title="Auto: the bot opens as soon as it starts — no target prices. Waiting: the bot waits until the market beats the target prices you enter on each leg. Locked while the bot runs.">
                        <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                    </Tooltip>
                </div>
                <EntryModeToggle
                    value={openMode}
                    disabled={pairReadOnly || lockedLive('open_mode')}
                    onChange={(next) => {
                        setOpenMode(next);
                        // Auto has no price condition: 0 (unlimited) would buy until the cash ran out.
                        if (next === 'IMMEDIATE' && maxOpen < 1) setMaxOpen(1);
                    }}
                />
            </div>

            <div data-tour="pair-cash" style={{ ...pairSection('legs'), marginTop: '8px' }}>
                {pairCapitalControls}
            </div>

            {/* Ended / ending pairs are history: every input below is disabled. */}
            <ConfigProvider componentDisabled={pairReadOnly}>

            {/* Spread + open-position PnL share one row: both are
                read-only market readouts, and stacking them cost two rows
                of the config column for four numbers. PnL moved here from
                the page header so the two live side by side. PnL still gets
                the wider half — it carries "+1,234,567 +12.34%" against a
                short spread — but only just: at 2fr the box was mostly
                gap. 1.2fr is about as tight as the long value fits. */}
            {/* Even halves. PnL abbreviates from a million up (below) so its figure and
                percent still fit the narrower half. */}
            <Grid data-tour="pair-readouts" $template="minmax(0, 1fr) minmax(0, 1fr)" $stackOnMobile={false} style={{ gap: '8px', marginTop: '8px', marginBottom: '8px' }}>
                <div style={{
                    padding: '8px 12px',
                    background: '#1E2329',
                    borderRadius: '4px',
                    border: '1px solid #2B3139',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    minWidth: 0,
                }}>
                    <span style={{ color: '#848E9C', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        Spread:
                        <Tooltip
                            overlayStyle={{ maxWidth: 340 }}
                            title={
                                <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Spread = futures ÷ basket − 1</div>
                                    <div>• <b>Futures</b>: 1 contract at the latest price (points × multiplier, in ₫).</div>
                                    <div>• <b>Basket</b>: your stocks at their weights, sized at the previous session&apos;s close (ATC) to be worth exactly that 1 contract, then valued at the latest prices.</div>
                                    <div style={{ marginTop: 4 }}>
                                        So the % is measured against the previous close: 0% means the two legs moved the same since then; +0.40% means the futures gained 0.40% more than your basket. It resets each session.
                                    </div>
                                </div>
                            }
                        >
                            <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                        </Tooltip>
                    </span>
                    <span
                        title={spreadPct == null
                            ? 'No futures quote, or no previous close for a basket stock yet'
                            : 'Futures ÷ your basket − 1, against the basket fixed at the previous close (ATC). Above 0 = futures rich vs basket.'}
                        style={{
                            // Not green/red: the sign is not a profit or a loss.
                            color: spreadPct == null ? '#848E9C' : spreadPct > 0 ? '#9085e9' : '#EAECEF',
                            fontSize: '14px',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {spreadPct == null ? '—' : `${spreadPct > 0 ? '+' : ''}${formatDecimal(spreadPct, 2)}%`}
                    </span>
                </div>
                <div style={{
                    padding: '8px 12px',
                    background: '#1E2329',
                    borderRadius: '4px',
                    border: '1px solid #2B3139',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    minWidth: 0,
                }}>
                    <span style={{ color: '#848E9C', fontSize: '12px', fontWeight: 600 }}>
                        PnL:
                    </span>
                    <span
                        // Exact figure stays reachable even when the shown one is abbreviated.
                        title={totalPnl == null ? 'No price for a leg yet' : `${totalPnl > 0 ? '+' : ''}${formatInt(totalPnl)}${totalReturnPct != null ? ` (${totalReturnPct > 0 ? '+' : ''}${formatDecimal(totalReturnPct, 2)}%)` : ''}`}
                        style={{
                            color: totalPnl != null && totalPnl > 0 ? '#0ECB81' : totalPnl != null && totalPnl < 0 ? '#F6465D' : '#848E9C',
                            fontSize: '14px',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {/* "-5,150,000 -2.13%" does not fit a half-width box, so from a million
                            up (always on a phone) it reads "-5.15M": the percent survives, and
                            the exact figure stays in the tooltip. */}
                        {totalPnl == null ? '—' : <>{totalPnl > 0 ? '+' : ''}{isMobile || Math.abs(totalPnl) >= 1e6 ? formatAbbrev(totalPnl) : formatInt(totalPnl)}</>}
                        {totalReturnPct != null && (
                            <Tooltip title="Return on the capital the open pairs tie up: the stock legs' full cash plus the futures legs' required margin at the broker floor. Book-wide — TP/SL is still evaluated per pair.">
                                <span style={{ color: '#848E9C', fontWeight: 600, marginLeft: '6px' }}>
                                    {totalReturnPct > 0 ? '+' : ''}{formatDecimal(totalReturnPct, 2)}%
                                </span>
                            </Tooltip>
                        )}
                    </span>
                </div>
            </Grid>

            {!isMobile && (
            <div style={{
                margin: '12px 0 8px',
                height: '1px',
                backgroundColor: '#2B3139',
                width: '100%'
            }} />
            )}

            {/* Futures leg — the contract this pair actually trades, with
                its reference price beside it. Placed above Stocks Search so
                the two legs read in order (future, then stocks) and the
                reference sits next to the symbol it belongs to instead of
                floating in the trigger block below. The symbol comes from
                the persisted deriv leg, so it follows an expiry-day roll. */}
            <div style={pairSection('legs')}>
            <div data-tour="pair-future" style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#EAECEF' }}>
                        Future
                    </span>
                    <span style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: futureLegIsShort ? '#F6465D' : '#0ECB81',
                    }}>
                        · {futureLegIsShort ? 'SHORT' : 'LONG'}
                    </span>
                    <Tooltip title={usesReference
                        ? 'Your reference price for this futures contract (index points). The bot opens a pair when the live-vs-reference PnL turns negative. Required before starting.'
                        : 'The bot shorts this contract at the best ask, then buys the stock basket at market once it fills.'}>
                        <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                    </Tooltip>
                </div>
                {/* One-row table, same shape as the stock leg below: the two
                    legs then read as one instrument list. Target price is the
                    operator's input (what the trigger compares against), and
                    QTY is the short exposure actually on the book. */}
                <LegTableWrapper>
                    <Table
                        dataSource={[{ key: 'future-leg' }]}
                        pagination={false}
                        size="small"
                        tableLayout="fixed"
                        columns={[
                            {
                                title: 'Symbol',
                                key: 'symbol',
                                // Without a width antd gives this column every leftover
                                // pixel, which is what pushed the two legs out of line.
                                width: LEG_COL.symbol,
                                render: () => (
                                    <LegSymbolCell
                                        price={futureLegMarketPrice != null ? formatDecimal(futureLegMarketPrice, 1) : '—'}
                                        priceMissing={futureLegMarketPrice == null}
                                        age={<PriceAge priceData={futureLegSymbol ? livePrices[futureLegSymbol] : null} />}
                                        name={
                                    <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                        <strong style={{ color: futureLegSymbol ? undefined : '#848E9C' }}>
                                            {futureLegSymbol ?? '—'}
                                        </strong>
                                        {futureLegExpiry && (
                                            <Tooltip
                                                title={
                                                    `This contract expired${futureLegExpiry.on ? ` on ${futureLegExpiry.on}` : ''}, so it no longer quotes — ` +
                                                    `the market price and the futures side of the reference PnL are frozen. ` +
                                                    `Roll the deriv leg${futureLegExpiry.rollTo ? ` to ${futureLegExpiry.rollTo}` : ''} and re-enter its target price.`
                                                }
                                            >
                                                <span style={{
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    padding: '0 3px',
                                                    borderRadius: 2,
                                                    border: '1px solid #F6465D',
                                                    color: '#F6465D',
                                                    cursor: 'help',
                                                }}>
                                                    EXPIRED
                                                </span>
                                            </Tooltip>
                                        )}
                                        {/* Live only on an expired contract — the one case the
                                            engine cannot fix itself. Kept visible (not hidden)
                                            while disabled so the tooltip can say where to go
                                            instead. */}
                                        <Tooltip title={pairReadOnly ? 'This pair has ended — read-only.' : rollTooltip}>
                                            <span
                                                role="button"
                                                aria-disabled={Boolean(rollBlock) || pairReadOnly}
                                                onClick={() => !rollBlock && !pairReadOnly && setRollModalOpen(true)}
                                                style={{
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    padding: '0 3px',
                                                    borderRadius: 2,
                                                    border: `1px solid ${rollBlock || pairReadOnly ? '#2B3139' : '#F0B90B'}`,
                                                    color: rollBlock || pairReadOnly ? '#484F5A' : '#F0B90B',
                                                    cursor: rollBlock || pairReadOnly ? 'not-allowed' : 'pointer',
                                                    userSelect: 'none',
                                                }}
                                            >
                                                ROLL
                                            </span>
                                        </Tooltip>
                                    </span>
                                        }
                                    />
                                ),
                            },
                            {
                                title: 'Target Price',
                                key: 'targetPrice',
                                width: LEG_COL.targetPrice,
                                // Every numeric column on both legs is right-aligned, so
                                // the digits line up down the page. An editable cell is
                                // no exception — it holds a price like the rest.
                                align: 'right' as const,
                                render: () => (
                                    <InputNumber
                                        style={{ width: '100%', fontWeight: 600 }}
                                        // Required before Start: without it the open trigger is undefined.
                                        className={`leg-price-input pair-edit-input${futureInstrumentPrice == null ? ' is-missing' : ''}`}
                                        size="small"
                                        value={futureInstrumentPrice}
                                        disabled={lockedLive('future_instrument_price')}
                                        onChange={(val) => setFutureInstrumentPrice(val ?? null)}
                                        min={0}
                                        step={0.1}
                                        // Same as the equity leg's target price: typed, not stepped.
                                        controls={false}
                                        placeholder="Required"
                                    />
                                ),
                            },
                            {
                                title: 'QTY',
                                key: 'qty',
                                width: LEG_COL.qtyOrProportion,
                                align: 'right' as const,
                                render: () => (
                                    <span style={{
                                        fontFamily: "'Courier New', monospace",
                                        fontWeight: 700,
                                        color: futureLegQty > 0
                                            ? (futureLegIsShort ? '#F6465D' : '#0ECB81')
                                            : '#848E9C',
                                    }}>
                                        {futureLegQty > 0
                                            ? `${futureLegIsShort ? '-' : '+'}${futureLegQty}`
                                            : '0'}
                                    </span>
                                ),
                            },
                            {
                                // Same rule as the stock rows: contracts held ⇒
                                // real unrealized PnL; flat ⇒ the OPEN trigger's
                                // futures contribution for ONE pair, badged.
                                title: (
                                    <Tooltip title="Contracts held show real unrealized PnL. Flat shows the OPEN trigger's futures leg for ONE pair (1 contract).">
                                        <span>{legPnlTitle}</span>
                                    </Tooltip>
                                ),
                                key: 'positionPnl',
                                width: LEG_COL.pnl,
                                align: 'right' as const,
                                render: () => (
                                    <PnlCellDisplay
                                        cell={resolvePnlCell({
                                            position: futureLegSymbol ? positionPnl[futureLegSymbol] : undefined,
                                            engineLeg: engineReferencePnl?.future
                                                ? {
                                                      pnl: engineReferencePnl.future.pnl,
                                                      quantity: engineReferencePnl.future.quantity,
                                                      side: engineReferencePnl.future.side,
                                                      // The row is labelled with the config's
                                                      // deriv_symbol; the engine may be on a
                                                      // different contract if a roll's report to
                                                      // the BE failed. Say so instead of showing
                                                      // the number under the wrong ticker.
                                                      note: engineFutureMismatch,
                                                  }
                                                : undefined,
                                            previewLeg: {
                                                quantity: 1,
                                                pnl: previewBreakdown.futurePnl,
                                            },
                                            engine: engineReferencePnl,
                                        })}
                                        quantityUnit="contract(s)"
                                        hidePerPairBadge={legPnlPerPair}
                                        costPrecision={1}
                                        undefinedReason={referencePnl.undefinedReason}
                                    />
                                ),
                            },
                        ].filter((c) => usesReference || c.key !== 'targetPrice')}
                    />
                </LegTableWrapper>
            </div>
            </div>

            {/* Futures expiry: warns two trading days out, reports an
                overdue roll, and offers the rebuild when a leg was left
                orphaned by settlement. Silent the rest of the month. */}
            <div style={pairSection('legs')}>
            <FuturesRollBanner
                compact
                status={rollPlan.status}
                busy={rollPlan.saving}
                onOpenPlan={() => setRollPlanModalOpen(true)}
                onOpenPairImport={() => setPairImportOpen(true)}
                onAcknowledge={() => { void rollPlan.acknowledge(); }}
            />
            </div>

            {/* Always-visible plan line: the roll is a scheduled action on
                someone else's calendar, so it should be readable without
                waiting for a warning to appear. */}
            <div style={pairSection('legs')}>
            {rollPlan.status && (
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: 8, fontSize: 11,
                    color: '#848E9C', marginBottom: 6,
                }}>
                    <span>{planSummary(rollPlan.status)}</span>
                    <Button
                        size="small"
                        type="link"
                        className="pair-subtle-link"
                        style={{ fontSize: 11, padding: 0, height: 'auto' }}
                        onClick={() => setRollPlanModalOpen(true)}
                    >
                        Roll settings
                    </Button>
                </div>
            )}
            </div>

            <div style={pairSection('legs')}>
            {chartContractLine && (
                <div style={{
                    fontSize: 11,
                    color: chartContract.engineAheadOfConfig ? '#F0B90B' : '#848E9C',
                    marginBottom: 6,
                }}>
                    {chartContractLine}
                </div>
            )}
            </div>

            <div style={pairSection('legs')}>
            <div data-tour="pair-equity" style={{ marginBottom: '8px', width: '100%', minWidth: 0 }}>
                {/* Heading sits OUTSIDE the card so it lines up with the
                    futures heading above — as a card title it rendered inside
                    the card head, indented past it. The stock leg trades
                    opposite the future (SELL future ⇒ LONG stocks), so the
                    side label is derived, never hard-coded. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#EAECEF' }}>
                        Equity
                    </span>
                    <span style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: futureLegIsShort ? '#0ECB81' : '#F6465D',
                    }}>
                        · {futureLegIsShort ? 'LONG' : 'SHORT'}
                    </span>
                    {/* Pushed to the far edge of the heading row: adding a symbol is
                        an action on this leg, so it belongs beside the leg's name
                        rather than in a row of its own above the table. */}
                    <span style={{ marginLeft: 'auto' }}>
                        <Popover
                            rootClassName="pair-add-stock-popover"
                            // Unmount the search on close, or its suggestion list outlives the popover.
                            destroyOnHidden
                            open={addStockOpen}
                            onOpenChange={(next) => {
                                if (structuralEditLocked) return;
                                setAddStockOpen(next);
                                if (!next) setAddStockQuery('');
                            }}
                            trigger="click"
                            placement="bottomLeft"
                            content={
                                <div style={{ width: 280 }}>
                                    <AutoComplete
                                        style={{ width: '100%' }}
                                        size="small"
                                        autoFocus
                                        value={addStockQuery}
                                        onChange={setAddStockQuery}
                                        options={stockSearchResults.map(s => ({
                                            value: s.symbol,
                                            label: `${s.symbol} - ₫${formatDecimal(s.price, 2)} (${s.changePercent24h >= 0 ? '+' : ''}${formatDecimal(s.changePercent24h, 2)}%)`
                                        }))}
                                        onSearch={handleStockSearch}
                                        onSelect={(val) => {
                                            handleSelectStock(val);
                                            setAddStockOpen(false);
                                            setAddStockQuery('');
                                        }}
                                        placeholder="Search stocks (e.g., VI, VN, FPT)..."
                                        notFoundContent={stockSearchLoading ? <Spin size="small" /> : null}
                                        suffixIcon={<SearchOutlined />}
                                    />
                                </div>
                            }
                        >
                            <Tooltip title={pairReadOnly ? 'This pair has ended — read-only.' : structuralEditLocked ? 'Stop the pair to add a symbol.' : undefined}>
                                {/* Labelled, because a bare plus above a table of symbols does
                                    not say what it adds. Height matches the other `small`
                                    controls in this panel. */}
                                <Button
                                    size="small"
                                    icon={<PlusOutlined />}
                                    disabled={structuralEditLocked}
                                    style={{
                                        flexShrink: 0,
                                        height: 22,
                                        padding: '0 8px',
                                        fontSize: 11,
                                        // Green only while it is usable: antd styles a disabled
                                        // button itself, and an inline colour would override that
                                        // and leave a bright border on a dead button.
                                        ...(structuralEditLocked
                                            ? {}
                                            : { borderColor: '#0ECB81', color: '#0ECB81' }),
                                    }}
                                >
                                    ADD
                                </Button>
                            </Tooltip>
                        </Popover>
                    </span>
                </div>
                {/* No card frame: the futures leg above is frameless too, and
                    the panel already sits inside the Trading Parameters card —
                    a third border only nested boxes. */}
                <div style={{ width: '100%' }}>

                    {selectedStocks.length > 0 && (
                        <>
                            {/* Full height, like the futures leg: the page scrolls, not the table. */}
                            <LegTableWrapper>
                                <Table
                                    dataSource={selectedStocks}
                                    columns={stockColumns}
                                    rowKey="symbol"
                                    pagination={false}
                                    size="small"
                                    tableLayout="fixed"
                                />
                            </LegTableWrapper>
                            <div style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                background: '#1E2329',
                                borderRadius: '4px',
                                border: `1px solid ${totalProportion === 100 ? '#0ECB81' : totalProportion > 100 ? '#F6465D' : '#2B3139'}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span style={{ color: '#848E9C', fontSize: '12px', fontWeight: 600 }}>
                                    Total Weight:
                                </span>
                                <ProportionValue $warning={totalProportion !== 1}>
                                    {formatDecimal(totalProportion, 1)} / 1
                                </ProportionValue>
                            </div>
                        </>
                    )}
                </div>
            </div>
            </div>

            {/* Σ of the legs' PnL column: the OPEN trigger reads straight off the target
                prices typed above, so it closes the legs block rather than the rules. */}
            {usesReference && (
            <div style={pairSection('legs')}>
            <ReferencePnlBanner
                compact
                total={referencePnl.total}
                source={referencePnl.source}
                undefinedReason={referencePnl.undefinedReason}
                blockedReason={referencePnl.blockedReason}
                ageSeconds={referencePnl.ageSeconds}
                unsavedEdits={referencePnl.unsavedEdits}
                frozenNote={referencePnlFrozenNote}
                isPause={isPause}
            />
            </div>
            )}

            {!isMobile && (
            <div style={{
                margin: '8px 0',
                height: '1px',
                backgroundColor: '#2B3139',
                width: '100%'
            }} />
            )}

            {/* Rules grouped by the decision they drive: opening, then closing. Max Holding
                Days used to sit beside Max Open, but it is a close rule. */}
            <div data-tour="pair-open" style={pairSection('trigger')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#EAECEF' }}>Opening</span>
                    <Tooltip title={usesReference
                        ? 'When the bot opens new pairs: while the reference PnL above is negative, up to Max Open Positions, unless paused.'
                        : 'When the bot opens new pairs: right after Start, one by one up to Max Open Positions in total, unless paused. A pair that closes does not free its slot, and the pair ends as soon as none is open.'}>
                        <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                    </Tooltip>
                </div>
            <Grid $columns={2} style={{ gap: '8px' }}>
                <FormGroup style={{ marginBottom: '6px' }}>
                    <Label style={{ fontSize: '11px', marginBottom: '0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Max Open Positions
                        <Tooltip title={usesReference
                            ? 'Maximum futures contracts open across all pairs. The bot keeps opening pair-by-pair while the reference PnL stays negative until this cap. 0 = unlimited (bounded only by cash, liquidity and the volume guard).'
                            : 'How many pairs (1 contract each) the bot opens after Start, in total. At least 1: with no price condition, this is the only limit on what it buys.'}>
                            <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                        </Tooltip>
                    </Label>
                    <InputNumber
                        className="pair-edit-input"
                        style={{ width: '100%' }}
                        size="small"
                        value={maxOpen}
                        disabled={lockedLive('max_open')}
                        onChange={(val) => setMaxOpen(val ?? (usesReference ? 0 : 1))}
                        min={usesReference ? 0 : 1}
                        step={1}
                    />
                </FormGroup>
                <FormGroup style={{ marginBottom: '6px' }}>
                    <Label style={{ fontSize: '11px', marginBottom: '0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Pause Opening
                        <Tooltip title="Freeze the open-pair trigger without stopping the bot. Existing pairs still close on TP/CL. Applies within ~1s while running.">
                            <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                        </Tooltip>
                    </Label>
                    <Switch
                        checked={isPause}
                        onChange={setIsPause}
                        checkedChildren="Paused"
                        unCheckedChildren="Active"
                    />
                </FormGroup>
            </Grid>
            </div>

            {!isMobile && (
            <div style={{
                margin: '8px 0',
                height: '1px',
                backgroundColor: '#2B3139',
                width: '100%'
            }} />
            )}

            <div data-tour="pair-exit" style={pairSection('risk')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#EAECEF' }}>Closing</span>
                    <Tooltip title="When an open pair is closed: on take-profit, stop-loss or after Max Holding Days. Close All closes every pair now.">
                        <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                    </Tooltip>
                </div>
            <Grid $columns={2} style={{ gap: '8px' }}>
                <FormGroup style={{ marginBottom: '6px' }}>
                    <Label style={{ fontSize: '11px', marginBottom: '0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        TP (%)
                        <Tooltip title="Take Profit percentage: the pair closes when its PnL reaches this gain. The percentage is measured against the capital the pair ties up: the stock leg's full cash plus the futures leg's required margin at the broker floor.">
                            <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                        </Tooltip>
                    </Label>
                    <InputNumber
                        className="pair-edit-input"
                        style={{ width: '100%' }}
                        size="small"
                        value={takeProfitReturn}
                        disabled={lockedLive('take_profit_return')}
                        onChange={(val) => setTakeProfitReturn(val || 0)}
                        min={0}
                        step={0.1}
                        precision={2}
                    />
                </FormGroup>
                <FormGroup style={{ marginBottom: '6px' }}>
                    <Label style={{ fontSize: '11px', marginBottom: '0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        SL (%)
                        <Tooltip title="Stop Loss percentage: the pair closes when its PnL falls to this loss. The percentage is measured against the capital the pair ties up: the stock leg's full cash plus the futures leg's required margin at the broker floor.">
                            <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                        </Tooltip>
                    </Label>
                    <InputNumber
                        className="pair-edit-input"
                        style={{ width: '100%' }}
                        size="small"
                        value={cutLossReturn}
                        disabled={lockedLive('cut_loss_return')}
                        onChange={(val) => setCutLossReturn(val || 0)}
                        min={0}
                        step={0.1}
                        precision={2}
                    />
                </FormGroup>
                <FormGroup style={{ marginBottom: '6px' }}>
                    <Label style={{ fontSize: '11px', marginBottom: '0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Max Holding Days
                        <Tooltip title="Auto force-close a pair once it has been held this many business days (open date → today, VN holidays excluded), even if take-profit / cut-loss never triggered. 0 = disabled (hold until TP/SL).">
                            <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                        </Tooltip>
                    </Label>
                    <InputNumber
                        className="pair-edit-input"
                        style={{ width: '100%' }}
                        size="small"
                        value={maxHoldingDays}
                        disabled={lockedLive('max_holding_days')}
                        onChange={(val) => setMaxHoldingDays(val ?? 0)}
                        min={0}
                        step={1}
                    />
                </FormGroup>
                <FormGroup style={{ marginBottom: '6px' }}>
                    <Label style={{ fontSize: '11px', marginBottom: '0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Close Positions
                        <Tooltip title="Close EVERY open pair at market now, ignoring TP/CL. The bot keeps running but stops opening new pairs — turn Pause Opening off to resume. Needs the bot running and continuous matching (not 11:30–13:00).">
                            <QuestionCircleOutlined style={{ fontSize: '12px', color: '#848E9C', cursor: 'help' }} />
                        </Tooltip>
                    </Label>
                    <Popconfirm
                        title="Close all positions"
                        description="Every open pair is closed at market now, ignoring TP/CL. The bot stays running but stops opening new pairs."
                        onConfirm={handleFlattenPair}
                        okText="Close all"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true, size: 'middle', loading: pairFlattenLoading, style: { minWidth: 72, height: 32 } }}
                        cancelButtonProps={{ size: 'middle', style: { minWidth: 72, height: 32, background: '#2B3139', borderColor: '#3d4148', color: '#E0E0E0' } }}
                        disabled={flattenDisabled || pairFlattenLoading}
                    >
                        <Button
                            size="small"
                            icon={<CloseCircleOutlined />}
                            loading={pairFlattenLoading}
                            disabled={flattenDisabled}
                            style={{
                                width: '100%',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: '24px',
                                borderRadius: '4px',
                                // Filled, with dark text for contrast on the yellow —
                                // same treatment as the Start button. Left `undefined`
                                // when disabled so antd's own muted styling shows.
                                background: flattenDisabled ? undefined : '#F0B90B',
                                borderColor: flattenDisabled ? undefined : '#F0B90B',
                                color: flattenDisabled ? undefined : '#1E2329',
                            }}
                        >
                            Close All
                        </Button>
                    </Popconfirm>
                </FormGroup>
            </Grid>
            </div>

            {/* Update/Save closes the rules: it commits what was edited above, including
                the Pause Opening switch. */}
            {!isMobile && updateButton}

            <div style={pairSection('orders')}>
            {botOrdersSection}
            </div>
            </ConfigProvider>

        </PairConfigScroll>
        {!isMobile && pairActionBar}
    </Card>
  );

  const pairDesktopTabs = (
    <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="pair-trading-tabs"
        size="small"
        style={{
            position: 'sticky',
            top: '0',
            zIndex: 100,
            background: 'linear-gradient(135deg, #1A1D23 0%, #1a1d26 25%, #0f1115 50%, #1a1d26 75%, #1A1D23 100%)',
            paddingTop: '4px',
            paddingLeft: '16px',
            paddingRight: '16px',
            marginTop: showTabs ? '0' : '-60px',
            opacity: showTabs ? 1 : 0,
            transition: 'all 0.3s ease',
            pointerEvents: showTabs ? 'auto' : 'none',
            visibility: showTabs ? 'visible' : 'hidden',
            flexShrink: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
        }}
        items={[
          {
            label: 'Search & Selection',
            key: '1',
            children: (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', minHeight: 0 }}>
                <ChartConfigSplit
                    stacked={isMobile}
                    drawerOpen={mobileConfigOpen}
                    onOpenDrawer={() => setMobileConfigOpen(true)}
                    onCloseDrawer={() => setMobileConfigOpen(false)}
                    configTitle="Trading Parameters"
                    initialSizes={[67, 33]}
                    minSizes={[300, 300]}
                    gap={2}
                    storageKey="pair:layout:chart-config"
                >
                    {/* First Column: Candlestick Chart with Bid/Ask */}
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%', position: 'relative', overflow: 'hidden' }}>
                        <ChartTabsSplit
                            stacked={isMobile}
                            initialSizes={[60, 40]}
                            minSizes={[200, 200]}
                            storageKey="pair:layout:chart-tabs:v2"
                            gap={2}
                        >
                            {/* Chart Section - Large. Always render the chart canvas
                                (even with no future selected → empty grid), mirroring
                                Grid's behaviour instead of swapping in a placeholder. */}
                            {pairChartNode}

                            {/* Bottom section under the chart — grid-style sub-tabs:
                                Logs (default) / Position / Order Book. Orders and Portfolio
                                stay as top-level tabs. */}
                            <div style={{
                                width: '100%', height: '100%', minHeight: 0,
                                display: 'flex', flexDirection: 'column', position: 'relative',
                                background: '#1E2329', border: '1px solid #2B3139', borderRadius: '4px',
                                overflow: 'hidden',
                            }}>
                                <Tabs
                                    activeKey={bottomTab}
                                    onChange={setBottomTab}
                                    className="pair-bottom-tabs"
                                    size="small"
                                    destroyOnHidden={false}
                                    tabBarStyle={{ margin: 0, padding: '0 8px', flexShrink: 0 }}
                                    style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
                                    items={pairBottomTabItems}
                                />
                            </div>
                        </ChartTabsSplit>
                    </div>

                    {/* Second Column: Trading Parameters */}
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0 }}>
                        {pairConfigCard}
                    </div>
                </ChartConfigSplit>
            </div>
            ),
          },
          {
            label: 'Order and Transactions',
            key: '2',
            children: (
              <OrderAndTransactionsTab
                selectedPortfolio={selectedPortfolio}
                active={activeTab === '2'}
                refreshKey={pairTabsRefreshKey}
                pollMs={pairBotActive ? PAIR_LIVE_POLL_MS : undefined}
              />
            ),
          },
          {
            label: 'Portfolio',
            key: '4',
            children: (
              <PortfolioTab
                selectedPortfolio={selectedPortfolio}
                active={activeTab === '4'}
                refreshKey={pairTabsRefreshKey}
                pollMs={pairBotActive ? PAIR_LIVE_POLL_MS : undefined}
                configType="PAIR"
                derivPortfolioId={derivPortfolioId}
              />
            ),
          },
          // Logs moved into the sub-tab area under the candlestick chart
          // (see "pair-bottom-tabs" above).
        ]}
    />
  );

  /**
   * Every destination that was a nested tab or a drawer becomes a pill: the config
   * groups (all served by the one card, so no remount), the readouts under the chart,
   * and the two page-level tables.
   */
  const pairMobileSections: ShellSection[] = [
      {
          key: 'legs',
          label: 'Legs',
          content: pairConfigCard,
          badge: settingsDirty ? <UnsavedDot /> : undefined,
      },
      { key: 'trigger', label: 'Opening', content: pairConfigCard },
      { key: 'risk', label: 'Closing', content: pairConfigCard },
      { key: 'orders', label: 'Bot orders', content: pairConfigCard },
      { key: 'position', label: 'Position', content: pairBottomTab('position'), ownScroll: true },
      { key: 'logs', label: 'Logs', content: pairBottomTab('logs'), ownScroll: true },
      { key: 'orderbook', label: 'Order Book', content: pairBottomTab('orderbook'), ownScroll: true },
      { key: 'balance', label: 'Import', content: pairBottomTab('balance'), ownScroll: true },
      {
          key: 'transactions',
          label: 'Transactions',
          hideChart: true,
          ownScroll: true,
          content: (
              <OrderAndTransactionsTab
                  selectedPortfolio={selectedPortfolio}
                  active={pairMobileSection === 'transactions'}
                  refreshKey={pairTabsRefreshKey}
                  pollMs={pairBotActive ? PAIR_LIVE_POLL_MS : undefined}
              />
          ),
      },
      {
          key: 'portfolio',
          label: 'Portfolio',
          hideChart: true,
          ownScroll: true,
          content: (
              <PortfolioTab
                  selectedPortfolio={selectedPortfolio}
                  active={pairMobileSection === 'portfolio'}
                  refreshKey={pairTabsRefreshKey}
                  pollMs={pairBotActive ? PAIR_LIVE_POLL_MS : undefined}
                  configType="PAIR"
                derivPortfolioId={derivPortfolioId}
              />
          ),
      },
  ];

  const pairMobileBody = (
      <MobileTradingShell
          storageKey="pair:mobile"
          // A touch taller than grid's: the pair chart spends ~76px on its header and pane
          // label before the canvas starts, where grid's spends almost none.
          chartHeightVh={36}
          chart={pairChartNode}
          sections={pairMobileSections}
          activeKey={pairMobileSection}
          onChange={setPairMobileSection}
          actionBar={
              PAIR_CONFIG_SECTIONS.includes(pairMobileSection) ? pairActionBar : undefined
          }
      />
  );

    return (
        <Container>
            <style>{`
                /* Scoped to .pair-trading-tabs so the layout-stretching + zero-padding
                   rules don't leak onto any other Tabs in the app. Mirrors the
                   pattern used by GridTrading (.grid-trading-tabs). Uses '>' direct-child
                   combinators so the rules apply ONLY to the top-level Tabs and do NOT
                   bleed into the nested .pair-bottom-tabs (Order Book + Logs) sub-tab. */
                .pair-trading-tabs,
                .pair-trading-tabs > .ant-tabs-content-holder,
                .pair-trading-tabs > .ant-tabs-content-holder > .ant-tabs-content,
                .pair-trading-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane-active {
                    flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden;
                    padding: 0;
                }
                .pair-trading-tabs > .ant-tabs-nav { flex-shrink: 0; }
                /* Bottom sub-tab under the chart (Order Book + Logs) fills its panel
                   and keeps its own nav. Direct-child scoped for the same reason. */
                .pair-bottom-tabs,
                .pair-bottom-tabs > .ant-tabs-content-holder,
                .pair-bottom-tabs > .ant-tabs-content-holder > .ant-tabs-content,
                .pair-bottom-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane-active {
                    flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden;
                    padding: 0;
                }
                .pair-bottom-tabs > .ant-tabs-nav { flex-shrink: 0; margin-bottom: 4px; }
                /* Update was styled to sit mid-form; in the pinned bar it is one of three
                   equal buttons, so drop the spacing it carried for that position. */
                .pair-mobile-actions > .ant-btn,
                .pair-mobile-actions > span > .ant-btn {
                    flex: 1; width: auto !important;
                    margin: 0 !important; height: 40px !important;
                }
                .pair-mobile-actions > span { flex: 1; display: flex; }
            `}</style>
            <TradingPageHeader
                pageName="Pair Trading"
                portfolios={equityPortfolios}
                selectedPortfolioId={selectedPortfolioId}
                onChangePortfolio={setSelectedPortfolioId}
                loadingPortfolios={loadingPortfolios}
                rightExtra={
                    <OnboardingTour
                        storageKey={PAIR_ONBOARDING_KEY}
                        steps={pairOnboardingSteps({
                            spreadDemo: {
                                start: () => {
                                    tourChartViewRef.current = chartView;
                                    setChartView('pnl');
                                    setTourDemoStock(true);
                                },
                                stop: () => {
                                    setTourDemoStock(false);
                                    if (tourChartViewRef.current) setChartView(tourChartViewRef.current);
                                    tourChartViewRef.current = null;
                                },
                            },
                        })}
                    />
                }
                /* PnL used to sit here; it now shares a row with Basis in the config
                   column so both live readouts are in one place. */
            />

            <PairSwitcherBar
                pairs={pairList.pairs}
                selectedKey={selectedPairKey}
                newTabOpen={newTabOpen}
                dirtyKeys={pairDirtyKeys}
                compact={isMobile}
                onSelect={(key) => { if (key !== selectedPairKey) selectPair(key); }}
                onNewPair={handleNewPair}
                onCloseNewPair={handleCloseNewPair}
                onCloseHistory={clearPair}
                error={pairList.error}
                onRetry={() => { void refreshPairList(); }}
            />

            {isMobile ? pairMobileBody : pairDesktopTabs}

            {/* Pair-level Deposit/Withdraw modal — driven from the Trading Parameters card header. */}
            <RollDerivLegModal
                open={rollModalOpen}
                onClose={() => { setRollModalOpen(false); setRollTargetSymbol(null); }}
                onRolled={() => {
                    // The roll rewrote the deriv symbol AND the stamped reference,
                    // so re-hydrate from the DB rather than patching local state —
                    // futureLegSymbol reads off existingPairConfig.deriv_symbol.
                    void fetchPairCashState();
                    void reloadPairSettings('futures roll');
                }}
                dataConfigId={pairDataConfigId}
                currentSymbol={futureLegSymbol}
                targetMarketPrice={livePriceOf(livePrices, rollTargetSymbol)}
                onTargetSymbolChange={setRollTargetSymbol}
                currentReferencePrice={
                    (existingPairConfig?.future_instrument_price as number | undefined) ?? null
                }
            />

            <RollPlanModal
                open={rollPlanModalOpen}
                onClose={() => setRollPlanModalOpen(false)}
                status={rollPlan.status}
                saving={rollPlan.saving}
                onSave={rollPlan.savePlan}
            />

            <ImportInProgressModal
                conflict={startImportConflict}
                onClose={() => setStartImportConflict(null)}
                onReady={() => {
                    void refreshPairList();
                    void fetchPositions();
                }}
            />

            <PairImportModal
                open={pairImportOpen}
                onClose={() => setPairImportOpen(false)}
                dataConfigId={pairDataConfigId}
                onImported={(newDataConfigId) => {
                    // The import created a NEW DataConfig — select it rather than
                    // patching state, or every later call would still address the
                    // archived one. The list must know it first (see usePairUrlState).
                    void (async () => {
                        await refreshPairList();
                        if (newDataConfigId) selectPair(newDataConfigId, { replace: true });
                    })();
                    void rollPlan.reload();
                }}
                stuckEnding={selectedPair?.lifecycleStatus === 'ENDING'}
                onEnded={() => {
                    appMessage.success('Pair ended. What the broker still holds now shows in the basic book.');
                    void refreshPairList();
                }}
            />

            <PairDepositWithdrawModal
                open={pairModalMode !== null}
                mode={pairModalMode ?? 'DEPOSIT'}
                dataConfigId={pairDataConfigId}
                onCancel={() => setPairModalMode(null)}
                onSuccess={handlePairCashSuccess}
            />

            {/* Start confirmation modal — config summary + schedule editor, like
                grid's "Place Grid — Order Summary". "Start Now" launches the bot. */}
            <Modal
                open={startConfirmVisible}
                title="Start Pair — Configuration Summary"
                onCancel={() => setStartConfirmVisible(false)}
                footer={null}
                width={560}
            >
                {configSummary}

                {startConfirmVisible && (
                    <AccountSnapshot
                        accounts={[
                            ...(selectedPortfolio ? [{ id: selectedPortfolio.id, label: `${selectedPortfolio.portfolioName} (stock)` }] : []),
                            ...(derivPortfolio ? [{ id: derivPortfolio.id, label: `${derivPortfolio.portfolioName} (derivatives)` }] : []),
                        ]}
                    />
                )}

                {/* Last screen before Start, so this is where an unadjusted corporate action
                    has to appear: pair's reference price is the mark the open trigger compares
                    against, and a stale one flips the sign of the signal. */}
                <CorporateActionPanel dataConfigId={pairDataConfigId} />

                {/* Same trigger, same component — this is the last screen before
                    Start, so the "will open immediately" warning matters most here. */}
                {usesReference ? (
                <ReferencePnlBanner
                    total={referencePnl.total}
                    source={referencePnl.source}
                    undefinedReason={referencePnl.undefinedReason}
                    blockedReason={referencePnl.blockedReason}
                    ageSeconds={referencePnl.ageSeconds}
                    unsavedEdits={referencePnl.unsavedEdits}
                    frozenNote={referencePnlFrozenNote}
                    isPause={isPause}
                />
                ) : (
                    <div
                        data-testid="pair-start-open-on-start"
                        style={{ background: '#2B1F0A', border: '1px solid #F0B90B', borderRadius: 4, padding: '8px 10px', marginBottom: 12, color: '#EAECEF', fontSize: 12 }}
                    >
                        {isPause
                            ? 'Opening is paused: the bot starts but opens nothing until you resume it.'
                            : `The bot will open up to ${maxOpen} pair${maxOpen === 1 ? '' : 's'} right after Start: short the futures at the best ask, then buy the basket at market.`}
                    </div>
                )}

                {/* Daily schedule config — only surfaced here in the Start Pair
                    modal, not inline in the Trading Parameters card. placePair
                    auto-creates a 9 AM entry on the BE; this lets the operator
                    edit/delete it at placement time. */}
                <div style={{ color: '#848E9C', fontSize: 11, marginBottom: 2 }}>Daily auto-start schedule (Mon–Fri)</div>
                {scheduleSection}

                {/* Unsaved edits: the summary above shows the form, but Start
                    would run the last SAVED config. Spell out the difference and
                    make the primary action persist-then-start, so the bot can
                    never launch on a configuration the operator did not see. */}
                {unsavedSettingsChanges.length > 0 && (
                    <div
                        data-testid="pair-start-unsaved-warning"
                        style={{ background: '#2B1F0A', border: '1px solid #F0B90B', borderRadius: 4, padding: '8px 10px', marginTop: 12 }}
                    >
                        <div style={{ color: '#F0B90B', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                            Unsaved configuration changes
                        </div>
                        <div style={{ color: '#EAECEF', fontSize: 12, marginBottom: 6 }}>
                            The summary above reflects your edits, but the bot only runs the saved
                            configuration. Confirm below to update the configuration and start with it,
                            or discard the edits to start with the saved one.
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16, color: '#EAECEF', fontSize: 12 }}>
                            {unsavedSettingsChanges.map((c) => (
                                <li key={c.label}>
                                    <span style={{ color: '#848E9C' }}>{c.label}:</span>{' '}
                                    <span style={{ textDecoration: 'line-through', color: '#848E9C' }}>{c.from}</span>
                                    {' → '}
                                    <span style={{ fontWeight: 600 }}>{c.to}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                    <Button
                        onClick={() => setStartConfirmVisible(false)}
                        disabled={startingPair || savingSettings}
                        style={{ flex: 1, height: 36 }}
                    >
                        Cancel
                    </Button>
                    {unsavedSettingsChanges.length > 0 && (
                        <Button
                            onClick={() => { void reloadPairSettings('discard unsaved edits'); }}
                            disabled={startingPair || savingSettings || loadingPairConfig}
                            style={{ flex: 1, height: 36 }}
                        >
                            Discard changes
                        </Button>
                    )}
                    <Button
                        type="primary"
                        loading={startingPair || savingSettings}
                        onClick={async () => {
                            const ok = unsavedSettingsChanges.length > 0
                                ? await handleUpdateAndStartPair()
                                : await handleStartPair();
                            // Keep the modal open on failure: the error toast explains
                            // why, and the operator can fix the form and retry.
                            if (ok) setStartConfirmVisible(false);
                        }}
                        style={{ flex: 1, height: 36, background: '#F0B90B', borderColor: '#F0B90B', color: '#1E2329', fontWeight: 600 }}
                    >
                        {unsavedSettingsChanges.length > 0 ? 'Update & Start' : 'Start Now'}
                    </Button>
                </div>
            </Modal>

            {/* Place Pair Result Modal */}
            <Modal
                // Three states share this modal — placing, succeeded, failed — but
                // the title only branched two ways, so `placePairResult` being null
                // mid-flight fell through to 'Error' and announced a failure over a
                // spinner that was still turning.
                title={
                    placingPair
                        ? 'Placing pair strategy'
                        : placePairResult?.success ? 'Success' : 'Error'
                }
                open={placePairModalVisible}
                onOk={handleClosePlacePairModal}
                onCancel={handleClosePlacePairModal}
                okText="Close"
                centered
                width={560}
                // antd v5 already pads .ant-modal-content 24px horizontally; a
                // second 24px here inset the body rows from the title.
                styles={{ body: { padding: '8px 0 0' } }}
                cancelButtonProps={{ style: { display: placePairResult?.success ? 'none' : 'inline-block' } }}
                okButtonProps={{
                    style: {
                        backgroundColor: placePairResult?.success ? '#0ECB81' : '#F6465D',
                        borderColor: placePairResult?.success ? '#0ECB81' : '#F6465D'
                    }
                }}
                maskClosable={!placingPair}
                closable={!placingPair}
                // No footer while placing, for the same reason the X and the mask
                // are disabled: `handleClosePlacePairModal` only hides the dialog,
                // it does not cancel the request. Dismissing mid-flight left the
                // placement running with nowhere to report back to.
                {...(placingPair ? { footer: null } : {})}
            >
                {placingPair ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: '16px', color: '#848E9C' }}>Placing pair strategy...</p>
                    </div>
                ) : placePairResult ? (
                    <div>
                        <p style={{
                            color: placePairResult.success ? '#0ECB81' : '#F6465D',
                            fontSize: '16px',
                            marginBottom: '16px'
                        }}>
                            {placePairResult.message}
                        </p>
                        {/* Recap of what was just saved. Replaces the old
                            "Stock Count" line, which showed the legacy
                            stockProportion state (always its 50 default). */}
                        {placePairResult.success && (
                            <>
                                <div style={{ color: '#848E9C', fontSize: 11, marginBottom: 6 }}>
                                    Saved configuration
                                </div>
                                {configSummary}
                                {placePairResult.data?.date && (
                                    <div style={{ color: '#848E9C', fontSize: 11, marginBottom: 12 }}>
                                        Trading date: {placePairResult.data.date}
                                    </div>
                                )}
                            </>
                        )}
                        {/* Daily auto-start schedule. placePair auto-creates a
                            9:00 AM weekday (Mon–Fri) entry on the BE; surface it
                            here so the operator can review/edit it right after
                            saving, without hunting through another screen. */}
                        {placePairResult.success && (
                            <div style={{ marginTop: 16 }}>
                                <div style={{ color: '#848E9C', fontSize: 11, marginBottom: 4 }}>
                                    Daily auto-start schedule (Mon–Fri)
                                </div>
                                {placePairResult.data?.scheduleCreated && (
                                    <div style={{ color: '#848E9C', fontSize: 11, marginBottom: 6, lineHeight: 1.4 }}>
                                        Created automatically at 09:00 on weekdays. Edit the time or remove it
                                        below if you prefer to start the pair by hand.
                                    </div>
                                )}
                                {scheduleSection}
                            </div>
                        )}
                    </div>
                ) : null}
            </Modal>
        </Container>
    );
};

export default PairTrading;

